import React, { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, writeBatch, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../hooks/useAuth';
import { PRODUCT_TYPES as BASE_PRODUCT_TYPES, CURRENCY_SYMBOL, CURRENCY_CODE, STOCK_TRACKED_PRODUCT_TYPES, STOCK_METER_PRODUCT_TYPES, STOCK_LOW_THRESHOLD_METERS, STOCK_LOW_THRESHOLD_PIECES } from '../../constants';
import { useLocalizedConstants } from '../../i18n/useLocalizedConstants';
import { useTranslation } from 'react-i18next';
import { formatPrice, formatUsdKztHint, formatProductListPrice, PRODUCT_PRICE_CURRENCY_USD } from '../../utils';
import { useExchangeRates } from '../../hooks/useExchangeRates';
import * as XLSX from 'xlsx';
import './ProductList.css';

const EXCEL_LEGACY_TR_COLUMNS = {
  productName: 'Ürün Adı',
  cost: 'Maliyet',
  unitPrice: 'Birim Fiyat',
  wholesalePrice: 'Toptan Fiyat',
  merdivenIpliCost: 'Merdiven İpli Maliyet',
  merdivenIpli: 'Merdiven İpli',
  merdivenIpliWholesale: 'Merdiven İpli Toptan',
  kurdeleliCost: 'Kurdeleli Maliyet',
  kurdeleli: 'Kurdeleli',
  kurdeleliWholesale: 'Kurdeleli Toptan',
  merdivenIpliRedCost: 'Merdiven İpli Redüktörlü Maliyet',
  merdivenIpliRed: 'Merdiven İpli Redüktörlü',
  merdivenIpliRedWholesale: 'Merdiven İpli Redüktörlü Toptan',
  kurdeleliRedCost: 'Kurdelalı Redüktörlü Maliyet',
  kurdeleliRed: 'Kurdelalı Redüktörlü',
  kurdeleliRed: 'Kurdelalı Redüktörlü',
  kurdeleliRedWholesale: 'Kurdelalı Redüktörlü Toptan',
  stock: 'Stok'
};

const isStockTrackedType = (productType) => STOCK_TRACKED_PRODUCT_TYPES.includes(productType);
const usesMeterStock = (productType) => STOCK_METER_PRODUCT_TYPES.includes(productType);

const isLowMeterStock = (stock) => {
  const value = parseFloat(stock);
  return Number.isFinite(value) && value < STOCK_LOW_THRESHOLD_METERS;
};

const isLowPieceStock = (stock) => {
  const value = parseFloat(stock);
  return Number.isFinite(value) && value < STOCK_LOW_THRESHOLD_PIECES;
};

// Ürün tipine göre düşük stok kontrolü (metre → 30m, adet → 2 adet)
const isLowStock = (stock, productType) =>
  usesMeterStock(productType) ? isLowMeterStock(stock) : isLowPieceStock(stock);

const formatStockDisplay = (stock, productType, t, formatPriceFn = formatPrice) => {
  const value = parseFloat(stock);
  if (!Number.isFinite(value)) return '—';
  const unit = usesMeterStock(productType)
    ? t('products.stock.unitMeterShort')
    : t('products.stock.unitPieceShort');
  return `${formatPriceFn(value)} ${unit}`;
};

const getExcelColumnHeader = (t, key) => t(`products.excel.columns.${key}`);

const getExcelRowValue = (row, key, t) => {
  const localizedHeader = getExcelColumnHeader(t, key);
  if (Object.prototype.hasOwnProperty.call(row, localizedHeader)) {
    return row[localizedHeader];
  }
  const legacyHeader = EXCEL_LEGACY_TR_COLUMNS[key];
  if (legacyHeader && Object.prototype.hasOwnProperty.call(row, legacyHeader)) {
    return row[legacyHeader];
  }
  return undefined;
};

const hasExcelColumn = (row, key, t) => {
  const localizedHeader = getExcelColumnHeader(t, key);
  if (localizedHeader in row) return true;
  const legacyHeader = EXCEL_LEGACY_TR_COLUMNS[key];
  return legacyHeader ? legacyHeader in row : false;
};

const buildExcelExportRow = (product, isJaluzi, t, activeTypeTab) => {
  const col = (key, value) => [getExcelColumnHeader(t, key), value];

  if (isJaluzi) {
    return Object.fromEntries([
      col('productName', product.name),
      col('merdivenIpliCost', product.merdivenIpliMaliyet || 0),
      col('merdivenIpli', product.merdivenIpliFiyat || 0),
      col('merdivenIpliWholesale', product.merdivenIpliToptan || 0),
      col('kurdeleliCost', product.kurdeleliMaliyet || 0),
      col('kurdeleli', product.kurdeleliFiyat || 0),
      col('kurdeleliWholesale', product.kurdeleliToptan || 0),
      col('merdivenIpliRedCost', product.merdivenIpliReduktorluMaliyet || 0),
      col('merdivenIpliRed', product.merdivenIpliReduktorluFiyat || 0),
      col('merdivenIpliRedWholesale', product.merdivenIpliReduktorluToptan || 0),
      col('kurdeleliRedCost', product.kurdelaliReduktorluMaliyet || 0),
      col('kurdeleliRed', product.kurdelaliReduktorluFiyat || 0),
      col('kurdeleliRedWholesale', product.kurdelaliReduktorluToptan || 0)
    ]);
  }

  const entries = [
    col('productName', product.name),
    col('cost', product.cost || 0),
    col('unitPrice', product.unitPrice),
    col('wholesalePrice', product.wholesalePrice || 0)
  ];

  if (isStockTrackedType(activeTypeTab)) {
    entries.push(col('stock', product.stock ?? 0));
  }

  return Object.fromEntries(entries);
};

const getExcelFormatErrorParams = (t) => ({
  productName: t('products.excel.columns.productName'),
  merdivenIpli: t('products.excel.columns.merdivenIpli'),
  kurdeleli: t('products.excel.columns.kurdeleli'),
  unitPrice: t('products.excel.columns.unitPrice')
});

const UsdPriceField = memo(({ label, value, onChange, disabled, exchangeRates, placeholder, style }) => (
  <div className="form-group">
    <label>{label}</label>
    <input
      type="number"
      step="0.01"
      min="0"
      className="modern-settings-input"
      value={value}
      onChange={onChange}
      disabled={disabled}
      placeholder={placeholder || '0.00'}
      style={style}
    />
    {formatUsdKztHint(value, exchangeRates, formatPrice) && (
      <small className="usd-kzt-hint">{formatUsdKztHint(value, exchangeRates, formatPrice)}</small>
    )}
  </div>
));

// --- Sub-Components (Memoized for Performance) ---
const PileSettingsPanel = memo(({ extraPrices, setExtraPrices, isSavingSettings, handleSaveSettings }) => {
  const { t } = useTranslation();

  const priceLabel = (key) => t(`products.prices.${key}`);
  const pileLabel = (key) => {
    if (key === 'american2') return t('products.pileNames.american2');
    if (key === 'american3') return t('products.pileNames.american3');
    return t(`constants.pileModels.${key}`);
  };

  const pileGroups = [
    { key: 'sPile', pileKey: 'sPile', data: extraPrices.sPile },
    { key: 'american2', pileKey: 'american2', data: extraPrices.american2 },
    { key: 'american3', pileKey: 'american3', data: extraPrices.american3 },
    { key: 'kapsul', pileKey: 'kapsulPile', data: extraPrices.kapsul }
  ];

  const atolyeDikimCost = extraPrices.atolyeDikimCost ?? '';

  return (
    <div className="settings-panel">
      <div className="settings-panel-header">
        <i className="fas fa-cut"></i>
        <h4>{t('products.pileExtraSettings')}</h4>
      </div>
      <div className="settings-grid">
        {pileGroups.map(({ key, pileKey, data }) => (
          <div key={key} className="price-group-wrapper">
            <span className="price-group-name"><i className="fas fa-layer-group" style={{ fontSize: '0.8rem' }}></i>{pileLabel(pileKey)}</span>
            <div className="price-group-grid">
              <div className="price-field"><label>{priceLabel('cost')}</label><input type="number" className="modern-settings-input" value={data.cost} onChange={(e) => setExtraPrices(p => ({ ...p, [key]: { ...p[key], cost: e.target.value } }))} /></div>
              <div className="price-field"><label>{priceLabel('retail')}</label><input type="number" className="modern-settings-input" value={data.retail} onChange={(e) => setExtraPrices(p => ({ ...p, [key]: { ...p[key], retail: e.target.value } }))} /></div>
              <div className="price-field"><label>{priceLabel('wholesale')}</label><input type="number" className="modern-settings-input" value={data.wholesale} onChange={(e) => setExtraPrices(p => ({ ...p, [key]: { ...p[key], wholesale: e.target.value } }))} /></div>
            </div>
          </div>
        ))}
        <div className="price-group-wrapper">
          <span className="price-group-name"><i className="fas fa-scissors" style={{ fontSize: '0.8rem' }}></i>{t('products.settings.atolyeDikimFee')}</span>
          <div className="price-group-grid price-group-grid-single">
            <div className="price-field"><label>{priceLabel('cost')}</label><input type="number" className="modern-settings-input" value={atolyeDikimCost} onChange={(e) => setExtraPrices(p => ({ ...p, atolyeDikimCost: e.target.value }))} /></div>
          </div>
        </div>
        <button className="btn-save" onClick={() => handleSaveSettings()} disabled={isSavingSettings} style={{ borderRadius: '10px' }}>
          {isSavingSettings ? '...' : t('common.save')}
        </button>
      </div>
    </div>
  );
});

const SettingsPriceGroup = memo(({ groupName, iconClass, values, onFieldChange }) => {
  const { t } = useTranslation();

  return (
    <div className="price-group-wrapper">
      <span className="price-group-name"><i className={iconClass} style={{ fontSize: '0.8rem' }}></i>{groupName}</span>
      <div className="price-group-grid">
        <div className="price-field"><label>{t('products.prices.cost')}</label><input type="number" className="modern-settings-input" value={values.cost} onChange={(e) => onFieldChange('cost', e.target.value)} /></div>
        <div className="price-field"><label>{t('products.prices.retail')}</label><input type="number" className="modern-settings-input" value={values.retail} onChange={(e) => onFieldChange('retail', e.target.value)} /></div>
        <div className="price-field"><label>{t('products.prices.wholesale')}</label><input type="number" className="modern-settings-input" value={values.wholesale} onChange={(e) => onFieldChange('wholesale', e.target.value)} /></div>
      </div>
    </div>
  );
});

const ProductListPrice = memo(({ amount, product, exchangeRates, className }) => {
  const { primary, hint } = formatProductListPrice(amount, product, exchangeRates, formatPrice);
  return (
    <span className={`product-list-price ${className || ''}`.trim()}>
      <span className="price-primary">{primary}</span>
      {hint && <small className="usd-kzt-hint">{hint}</small>}
    </span>
  );
});

const mechanismPriceProduct = (mechanism) =>
  mechanism?.priceCurrency === PRODUCT_PRICE_CURRENCY_USD ? { priceCurrency: PRODUCT_PRICE_CURRENCY_USD } : {};

const getAtolyeDikimFromSettings = (data, productType) =>
  data[`${productType}AtolyeDikimCost`] ?? '';

const ProductRow = ({ product, index, activeTypeTab, canManageProducts, onEdit, onDelete, formatPrice, exchangeRates, showLowStockHighlight }) => {
  const { t } = useTranslation();

  return (
    <tr key={product.id}>
      <td className="row-num">{index + 1}</td>
      <td className="product-name">{product.name}</td>

      {(activeTypeTab === 'ahsapJaluzi') ? (
        <>
          {[
            { label: 'merdivenIpli', cost: product.merdivenIpliMaliyet, price: product.merdivenIpliFiyat, wholesale: product.merdivenIpliToptan },
            { label: 'kurdeleli', cost: product.kurdeleliMaliyet, price: product.kurdeleliFiyat, wholesale: product.kurdeleliToptan },
            { label: 'merdivenIpliReduktorlu', cost: product.merdivenIpliReduktorluMaliyet, price: product.merdivenIpliReduktorluFiyat, wholesale: product.merdivenIpliReduktorluToptan },
            { label: 'kurdelaliReduktorlu', cost: product.kurdelaliReduktorluMaliyet, price: product.kurdelaliReduktorluFiyat, wholesale: product.kurdelaliReduktorluToptan }
          ].map((variant, i) => (
            <td key={i} className="product-price-cell senior-design">
              <div className="price-group-vertical">
                <div className="price-row cost" title={t('products.prices.cost')}>
                  <span className="price-icon"><i className="fas fa-coins"></i></span>
                  <ProductListPrice amount={variant.cost} product={product} exchangeRates={exchangeRates} className="price-value" />
                </div>
                <div className="price-row retail" title={t('products.prices.retailSale')}>
                  <span className="price-icon"><i className="fas fa-tag"></i></span>
                  <ProductListPrice amount={variant.price} product={product} exchangeRates={exchangeRates} className="price-value" />
                </div>
                <div className="price-row wholesale" title={t('products.prices.wholesaleSale')}>
                  <span className="price-icon"><i className="fas fa-percent"></i></span>
                  <ProductListPrice amount={variant.wholesale} product={product} exchangeRates={exchangeRates} className="price-value" />
                </div>
              </div>
            </td>
          ))}
        </>
      ) : activeTypeTab === 'rustik' ? (
        isStockTrackedType(activeTypeTab) ? (
          <td className="product-stock">
            <span className={`stock-badge${showLowStockHighlight && isLowStock(product.stock, activeTypeTab) ? ' stock-low' : ''}`}>
              {formatStockDisplay(product.stock, product.productType, t, formatPrice)}
            </span>
          </td>
        ) : null
      ) : (
        <>
          <td className="product-cost">
            {product.cost > 0
              ? <span className="cost-badge"><ProductListPrice amount={product.cost} product={product} exchangeRates={exchangeRates} /></span>
              : <span className="no-cost">—</span>
            }
          </td>
          <td className="product-price">
            <span className="price-badge"><ProductListPrice amount={product.unitPrice} product={product} exchangeRates={exchangeRates} /></span>
          </td>
          <td className="product-price">
            {product.wholesalePrice > 0
              ? <span className="price-badge wholesale-badge"><ProductListPrice amount={product.wholesalePrice} product={product} exchangeRates={exchangeRates} /></span>
              : <span className="no-cost">—</span>
            }
          </td>
          {isStockTrackedType(activeTypeTab) && (
            <td className="product-stock">
              <span className={`stock-badge${showLowStockHighlight && isLowStock(product.stock, activeTypeTab) ? ' stock-low' : ''}`}>
                {formatStockDisplay(product.stock, product.productType, t, formatPrice)}
              </span>
            </td>
          )}
        </>
      )}
      {canManageProducts && (
        <td className="product-actions">
          <button className="btn-edit-product" onClick={() => onEdit(product)} title={t('common.edit')}>
            <i className="fas fa-edit"></i>
          </button>
          <button className="btn-delete-product" onClick={() => onDelete(product)} title={t('common.delete')}>
            <i className="fas fa-trash"></i>
          </button>
        </td>
      )}
    </tr>
  );
};

const ProductListTable = ({
  filteredProducts,
  activeTypeTab,
  canManageProducts,
  handleEdit,
  setDeleteModal,
  formatPrice,
  exchangeRates
}) => {
  const { t } = useTranslation();

  if (filteredProducts.length === 0) return null;

  return (
    <table className="product-table">
      <thead>
        <tr>
          <th>#</th>
          <th>{t('products.table.productName')}</th>
          {activeTypeTab === 'ahsapJaluzi' ? (
            <>
              <th>{t('products.table.merdivenIpli')}</th>
              <th>{t('products.table.kurdeleli')}</th>
              <th>{t('products.table.merdivenIpliRed')}</th>
              <th>{t('products.table.kurdeleliRed')}</th>
            </>
          ) : activeTypeTab === 'rustik' ? (
            isStockTrackedType(activeTypeTab) ? (
              <th>{t('products.table.stock')}</th>
            ) : null
          ) : (
            <>
              <th>{t('products.prices.cost')}</th>
              <th>{t('products.prices.unitPrice')}</th>
              <th>{t('products.prices.wholesalePrice')}</th>
              {isStockTrackedType(activeTypeTab) && <th>{t('products.table.stock')}</th>}
            </>
          )}
          {canManageProducts && <th>{t('products.table.actions')}</th>}
        </tr>
      </thead>
      <tbody>
        {filteredProducts.map((product, index) => (
          <ProductRow
            key={product.id}
            product={product}
            index={index}
            activeTypeTab={activeTypeTab}
            canManageProducts={canManageProducts}
            onEdit={handleEdit}
            onDelete={setDeleteModal}
            formatPrice={formatPrice}
            exchangeRates={exchangeRates}
            showLowStockHighlight={isStockTrackedType(activeTypeTab)}
          />
        ))}
      </tbody>
    </table>
  );
};

const ProductList = () => {
  const { t } = useTranslation();
  const { permissions } = useAuth();
  const canManageProducts = permissions?.canManageProducts;
  const canViewProductSettings = permissions?.canAccessProducts;
  const settingsReadOnly = !canManageProducts;
  const { PRODUCT_TYPES } = useLocalizedConstants();

  const getCategoryLabel = useCallback((key) => (
    PRODUCT_TYPES[key]?.label
  ), [PRODUCT_TYPES]);

  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExcelLoading, setIsExcelLoading] = useState(false);
  const [activeTypeTab, setActiveTypeTab] = useState(Object.keys(BASE_PRODUCT_TYPES)[0]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const fileInputRef = useRef(null);

  const [extraPrices, setExtraPrices] = useState({
    sPile: { cost: '', retail: '', wholesale: '' },
    american2: { cost: '', retail: '', wholesale: '' },
    american3: { cost: '', retail: '', wholesale: '' },
    kapsul: { cost: '', retail: '', wholesale: '' },
    atolyeDikimCost: ''
  });
  const globalSettingsRef = useRef({});
  const [ahsapJaluziExtras, setAhsapJaluziExtras] = useState({
    zigil: { cost: '', retail: '', wholesale: '' }
  });
  const [metalJaluziExtras, setMetalJaluziExtras] = useState({
    zigil: { cost: '', retail: '', wholesale: '' },
    reduktor: { cost: '', retail: '', wholesale: '' },
    ikiCamArasi: { cost: '', retail: '', wholesale: '' }
  });
  const {
    exchangeRates,
    manualOverrides,
    ratesVersion,
    setManualRate,
    resetManualRate,
    refreshRates
  } = useExchangeRates({ formatForDisplay: true, includeMeta: true });
  const [isRefreshingRates, setIsRefreshingRates] = useState(false);
  const [rateDrafts, setRateDrafts] = useState({ USD: '', EUR: '' });

  useEffect(() => {
    setRateDrafts({
      USD: exchangeRates.USD || '',
      EUR: exchangeRates.EUR || ''
    });
  }, [exchangeRates.USD, exchangeRates.EUR, ratesVersion]);

  const handleRefreshExchangeRates = async () => {
    if (isRefreshingRates) return;
    setIsRefreshingRates(true);
    try {
      await refreshRates();
    } catch (error) {
      console.error('Döviz kurları yenilenemedi:', error);
    } finally {
      setIsRefreshingRates(false);
    }
  };

  const commitManualRate = (currency) => {
    const draft = rateDrafts[currency];
    if (draft === '' || draft === null || draft === undefined) {
      resetManualRate(currency);
      return;
    }
    setManualRate(currency, draft);
  };

  const handleRateDraftChange = (currency, value) => {
    setRateDrafts((prev) => ({ ...prev, [currency]: value }));
  };
  const [storExtras, setStorExtras] = useState({
    jumbo: { cost: '', retail: '', wholesale: '' },
    altEtekDilim: { cost: '', retail: '', wholesale: '' },
    kapaliKasa: { cost: '', retail: '', wholesale: '' },
    altEtekLazer: { cost: '', retail: '', wholesale: '' },
    dekoratifLama: { cost: '', retail: '', wholesale: '' },
    dekoratifKasa: { cost: '', retail: '', wholesale: '' },
    altEtekBeyazIs: { cost: '', retail: '', wholesale: '' }
  });
  const [zebraExtras, setZebraExtras] = useState({
    jumbo: { cost: '', retail: '', wholesale: '' },
    altEtekDilim: { cost: '', retail: '', wholesale: '' },
    kapaliKasa: { cost: '', retail: '', wholesale: '' }
  });
  const [dikeyExtras, setDikeyExtras] = useState({
    fon: { cost: '', retail: '', wholesale: '' },
    fonList: []
  });
  const [katlamaliExtras, setKatlamaliExtras] = useState({ mekanizmaList: [] });
  const [newKatlamaliMekanizma, setNewKatlamaliMekanizma] = useState({ type: '', cost: '', price: '', wholesale: '' });
  const [isKatlamaliMekanizmaCollapsed, setIsKatlamaliMekanizmaCollapsed] = useState(true);
  const [rustikExtras, setRustikExtras] = useState({
    ayakModelList: [],
    ayakCapList: [],
    ayakUzunlukList: [],
    ayakRenkKoduList: []
  });
  const [motorSettings, setMotorSettings] = useState({
    motor: { cost: '', retail: '', wholesale: '', currency: 'USD', specialRateRetail: '', specialRateWholesale: '' },
    stor: { cost: '', retail: '', wholesale: '', currency: 'USD', specialRateRetail: '', specialRateWholesale: '' },
    zebra: { cost: '', retail: '', wholesale: '', currency: 'USD', specialRateRetail: '', specialRateWholesale: '' },
    ahsapJaluzi: { cost: '', retail: '', wholesale: '', currency: 'USD', specialRateRetail: '', specialRateWholesale: '' },
    metalJaluzi: { cost: '', retail: '', wholesale: '', currency: 'USD', specialRateRetail: '', specialRateWholesale: '' },
    dikeyPerde: { cost: '', retail: '', wholesale: '', currency: 'USD', specialRateRetail: '', specialRateWholesale: '' },
    dikeyPvc: { cost: '', retail: '', wholesale: '', currency: 'USD', specialRateRetail: '', specialRateWholesale: '' },
    dikeyStor: { cost: '', retail: '', wholesale: '', currency: 'USD', specialRateRetail: '', specialRateWholesale: '' },
    katlamaliPerde: { cost: '', retail: '', wholesale: '', currency: 'USD', specialRateRetail: '', specialRateWholesale: '' }
  });
  const [newRustikModel, setNewRustikModel] = useState('');
  const [newRustikCap, setNewRustikCap] = useState('');
  const [newRustikUzunluk, setNewRustikUzunluk] = useState('');
  const [newRustikRenk, setNewRustikRenk] = useState('');
  const [isRustikSettingsCollapsed, setIsRustikSettingsCollapsed] = useState(true);
  const [isRustikModelCollapsed, setIsRustikModelCollapsed] = useState(true);
  const [isRustikCapCollapsed, setIsRustikCapCollapsed] = useState(true);
  const [isRustikUzunlukCollapsed, setIsRustikUzunlukCollapsed] = useState(true);
  const [isRustikRenkCollapsed, setIsRustikRenkCollapsed] = useState(true);

  const [editingRustikModel, setEditingRustikModel] = useState({ index: null, value: '' });
  const [editingRustikCap, setEditingRustikCap] = useState({ index: null, value: '' });
  const [editingRustikUzunluk, setEditingRustikUzunluk] = useState({ index: null, value: '' });
  const [editingRustikRenk, setEditingRustikRenk] = useState({ index: null, value: '' });

  useEffect(() => {
    // Fetch Settings
    const fetchSettings = async () => {
      try {
        const settingsRef = doc(db, 'settings', 'global');
        const docSnap = await getDoc(settingsRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          globalSettingsRef.current = data;
          setExtraPrices({
            sPile: { cost: data.sPileCost || '', retail: data.sPilePrice || '', wholesale: data.sPileWholesale || '' },
            american2: { cost: data.american2Cost || '', retail: data.american2Price || '', wholesale: data.american2Wholesale || '' },
            american3: { cost: data.american3Cost || '', retail: data.american3Price || '', wholesale: data.american3Wholesale || '' },
            kapsul: { cost: data.kapsulCost || '', retail: data.kapsulPrice || '', wholesale: data.kapsulWholesale || '' },
            atolyeDikimCost: getAtolyeDikimFromSettings(data, activeTypeTab === 'kanat' ? 'kanat' : 'tul')
          });
          setAhsapJaluziExtras({
            zigil: { cost: data.zigilCost || '', retail: data.zigilPrice || '', wholesale: data.zigilWholesale || '' },
            reduktor: { cost: data.reduktorCost || '', retail: data.reduktorPrice || '', wholesale: data.reduktorWholesale || '' },
            kurdele: { cost: data.kurdelaCost || '', retail: data.kurdelaPrice || '', wholesale: data.kurdelaWholesale || '' }
          });
          setMetalJaluziExtras({
            zigil: { cost: data.metalZigilCost || '', retail: data.metalZigilPrice || '', wholesale: data.metalZigilWholesale || '' },
            reduktor: { cost: data.metalReduktorCost || '', retail: data.metalReduktorPrice || '', wholesale: data.metalReduktorWholesale || '' },
            ikiCamArasi: { cost: data.ikiCamArasiCost || '', retail: data.ikiCamArasiPrice || '', wholesale: data.ikiCamArasiWholesale || '' }
          });
          setStorExtras({
            jumbo: { cost: data.storJumboCost || '', retail: data.storJumboPrice || '', wholesale: data.storJumboWholesale || '' },
            altEtekDilim: { cost: data.storAltEtekDilimCost || '', retail: data.storAltEtekDilimPrice || '', wholesale: data.storAltEtekDilimWholesale || '' },
            kapaliKasa: { cost: data.storKapaliKasaCost || '', retail: data.storKapaliKasaPrice || '', wholesale: data.storKapaliKasaWholesale || '' },
            altEtekLazer: { cost: data.storAltEtekLazerCost || '', retail: data.storAltEtekLazerPrice || '', wholesale: data.storAltEtekLazerWholesale || '' },
            dekoratifLama: { cost: data.storDekoratifLamaCost || '', retail: data.storDekoratifLamaPrice || '', wholesale: data.storDekoratifLamaWholesale || '' },
            dekoratifKasa: { cost: data.storDekoratifKasaCost || '', retail: data.storDekoratifKasaPrice || '', wholesale: data.storDekoratifKasaWholesale || '' },
            altEtekBeyazIs: { cost: data.storAltEtekBeyazIsCost || '', retail: data.storAltEtekBeyazIsPrice || '', wholesale: data.storAltEtekBeyazIsWholesale || '' }
          });
          setZebraExtras({
            jumbo: { cost: data.zebraJumboCost || '', retail: data.zebraJumboPrice || '', wholesale: data.zebraJumboWholesale || '' },
            altEtekDilim: { cost: data.zebraAltEtekDilimCost || '', retail: data.zebraAltEtekDilimPrice || '', wholesale: data.zebraAltEtekDilimWholesale || '' },
            kapaliKasa: { cost: data.zebraKapaliKasaCost || '', retail: data.zebraKapaliKasaPrice || '', wholesale: data.zebraKapaliKasaWholesale || '' }
          });
          setDikeyExtras({
            fon: { cost: data.dikeyFonCost || '', retail: data.dikeyFonPrice || '', wholesale: data.dikeyFonWholesale || '' },
            fonList: data.dikeyFonList || []
          });
          setKatlamaliExtras({
            mekanizmaList: data.katlamaliMekanizmaList || []
          });
          setRustikExtras({
            ayakModelList: data.rustikAyakModelList || [],
            ayakCapList: data.rustikAyakCapList || [],
            ayakUzunlukList: data.rustikAyakUzunlukList || [],
            ayakRenkKoduList: data.rustikAyakRenkKoduList || []
          });
          const loadedMotorSettings = {};
          ['stor', 'zebra', 'ahsapJaluzi', 'metalJaluzi', 'dikeyPerde', 'dikeyPvc', 'dikeyStor', 'katlamaliPerde'].forEach(type => {
            loadedMotorSettings[type] = {
              cost: data[`${type}MotorCost`] !== undefined ? data[`${type}MotorCost`] : '',
              retail: data[`${type}MotorRetail`] !== undefined ? data[`${type}MotorRetail`] : '',
              wholesale: data[`${type}MotorWholesale`] !== undefined ? data[`${type}MotorWholesale`] : '',
              currency: data[`${type}MotorCurrency`] || 'USD',
              specialRateRetail: data[`${type}MotorSpecialRateRetail`] || '',
              specialRateWholesale: data[`${type}MotorSpecialRateWholesale`] || ''
            };
          });
          setMotorSettings(loadedMotorSettings);
        }
      } catch (err) {
        console.error('Settings fetch error:', err);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    if (activeTypeTab !== 'tul' && activeTypeTab !== 'kanat') return;
    const data = globalSettingsRef.current || {};
    setExtraPrices((prev) => ({
      ...prev,
      atolyeDikimCost: getAtolyeDikimFromSettings(data, activeTypeTab)
    }));
  }, [activeTypeTab]);

  const handleSaveMotorSettings = async (type) => {
    try {
      setIsSavingSettings(true);
      const settingsRef = doc(db, 'settings', 'global');
      const settings = motorSettings[type];

      const updateData = {
        [`${type}MotorCost`]: Number(settings.cost),
        [`${type}MotorRetail`]: Number(settings.retail),
        [`${type}MotorWholesale`]: Number(settings.wholesale),
        [`${type}MotorCurrency`]: settings.currency,
        [`${type}MotorSpecialRateRetail`]: Number(settings.specialRateRetail),
        [`${type}MotorSpecialRateWholesale`]: Number(settings.specialRateWholesale)
      };

      await updateDoc(settingsRef, updateData);
      setExcelModal({ show: true, type: 'success', title: t('common.success'), message: t('products.modal.motorSettingsUpdated', { type: PRODUCT_TYPES[type]?.label || type }) });
    } catch (error) {
      console.error('Ayarlar kaydedilirken hata:', error);
      setExcelModal({ show: true, type: 'error', title: t('common.error'), message: t('products.modal.settingsSaveFailed', { message: error.message }) });
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleSaveSettings = async (overrideData = null) => {
    setIsSavingSettings(true);
    try {
      const settingsRef = doc(db, 'settings', 'global');
      let updateData = overrideData || {};

      if (!overrideData) {
        const typeKeys = {
          ahsapJaluzi: 'ahsapJaluzi',
          metalJaluzi: 'metalJaluzi',
          stor: 'stor',
          zebra: 'zebra',
          dikeyPerde: 'dikeyPerde',
          dikeyPvc: 'dikeyPvc',
          dikeyStor: 'dikeyStor',
          katlamaliPerde: 'katlamaliPerde'
        };

        if (typeKeys[activeTypeTab]) {
          const type = typeKeys[activeTypeTab];
          const settings = motorSettings[type];
          updateData[`${type}MotorCost`] = Number(settings.cost);
          updateData[`${type}MotorRetail`] = Number(settings.retail);
          updateData[`${type}MotorWholesale`] = Number(settings.wholesale);
          updateData[`${type}MotorCurrency`] = settings.currency;
          updateData[`${type}MotorSpecialRateRetail`] = Number(settings.specialRateRetail);
          updateData[`${type}MotorSpecialRateWholesale`] = Number(settings.specialRateWholesale);
        }

        if (activeTypeTab === 'tul' || activeTypeTab === 'kanat') {
          updateData = {
            ...updateData,
            sPileCost: Number(extraPrices.sPile.cost) || 0,
            sPilePrice: Number(extraPrices.sPile.retail) || 0,
            sPileWholesale: Number(extraPrices.sPile.wholesale) || 0,
            american2Cost: Number(extraPrices.american2.cost) || 0,
            american2Price: Number(extraPrices.american2.retail) || 0,
            american2Wholesale: Number(extraPrices.american2.wholesale) || 0,
            american3Cost: Number(extraPrices.american3.cost) || 0,
            american3Price: Number(extraPrices.american3.retail) || 0,
            american3Wholesale: Number(extraPrices.american3.wholesale) || 0,
            kapsulCost: Number(extraPrices.kapsul.cost) || 0,
            kapsulPrice: Number(extraPrices.kapsul.retail) || 0,
            kapsulWholesale: Number(extraPrices.kapsul.wholesale) || 0,
            [`${activeTypeTab}AtolyeDikimCost`]: Number(extraPrices.atolyeDikimCost) || 0
          };
        } else if (activeTypeTab === 'ahsapJaluzi') {
          updateData = {
            ...updateData,
            zigilCost: Number(ahsapJaluziExtras.zigil.cost),
            zigilPrice: Number(ahsapJaluziExtras.zigil.retail),
            zigilWholesale: Number(ahsapJaluziExtras.zigil.wholesale),
            reduktorCost: Number(ahsapJaluziExtras.reduktor.cost),
            reduktorPrice: Number(ahsapJaluziExtras.reduktor.retail),
            reduktorWholesale: Number(ahsapJaluziExtras.reduktor.wholesale),
            kurdelaCost: Number(ahsapJaluziExtras.kurdele.cost),
            kurdelaPrice: Number(ahsapJaluziExtras.kurdele.retail),
            kurdelaWholesale: Number(ahsapJaluziExtras.kurdele.wholesale)
          };
        } else if (activeTypeTab === 'metalJaluzi') {
          updateData = {
            ...updateData,
            metalZigilCost: Number(metalJaluziExtras.zigil.cost),
            metalZigilPrice: Number(metalJaluziExtras.zigil.retail),
            metalZigilWholesale: Number(metalJaluziExtras.zigil.wholesale),
            metalReduktorCost: Number(metalJaluziExtras.reduktor.cost),
            metalReduktorPrice: Number(metalJaluziExtras.reduktor.retail),
            metalReduktorWholesale: Number(metalJaluziExtras.reduktor.wholesale),
            ikiCamArasiCost: Number(metalJaluziExtras.ikiCamArasi.cost),
            ikiCamArasiPrice: Number(metalJaluziExtras.ikiCamArasi.retail),
            ikiCamArasiWholesale: Number(metalJaluziExtras.ikiCamArasi.wholesale)
          };
        } else if (activeTypeTab === 'stor') {
          updateData = {
            ...updateData,
            storJumboCost: Number(storExtras.jumbo.cost),
            storJumboPrice: Number(storExtras.jumbo.retail),
            storJumboWholesale: Number(storExtras.jumbo.wholesale),
            storAltEtekDilimCost: Number(storExtras.altEtekDilim.cost),
            storAltEtekDilimPrice: Number(storExtras.altEtekDilim.retail),
            storAltEtekDilimWholesale: Number(storExtras.altEtekDilim.wholesale),
            storKapaliKasaCost: Number(storExtras.kapaliKasa.cost),
            storKapaliKasaPrice: Number(storExtras.kapaliKasa.retail),
            storKapaliKasaWholesale: Number(storExtras.kapaliKasa.wholesale),
            storAltEtekLazerCost: Number(storExtras.altEtekLazer.cost),
            storAltEtekLazerPrice: Number(storExtras.altEtekLazer.retail),
            storAltEtekLazerWholesale: Number(storExtras.altEtekLazer.wholesale),
            storDekoratifLamaCost: Number(storExtras.dekoratifLama.cost),
            storDekoratifLamaPrice: Number(storExtras.dekoratifLama.retail),
            storDekoratifLamaWholesale: Number(storExtras.dekoratifLama.wholesale),
            storDekoratifKasaCost: Number(storExtras.dekoratifKasa.cost),
            storDekoratifKasaPrice: Number(storExtras.dekoratifKasa.retail),
            storDekoratifKasaWholesale: Number(storExtras.dekoratifKasa.wholesale),
            storAltEtekBeyazIsCost: Number(storExtras.altEtekBeyazIs.cost),
            storAltEtekBeyazIsPrice: Number(storExtras.altEtekBeyazIs.retail),
            storAltEtekBeyazIsWholesale: Number(storExtras.altEtekBeyazIs.wholesale)
          };
        } else if (activeTypeTab === 'zebra') {
          updateData = {
            ...updateData,
            zebraJumboCost: Number(zebraExtras.jumbo.cost),
            zebraJumboPrice: Number(zebraExtras.jumbo.retail),
            zebraJumboWholesale: Number(zebraExtras.jumbo.wholesale),
            zebraAltEtekDilimCost: Number(zebraExtras.altEtekDilim.cost),
            zebraAltEtekDilimPrice: Number(zebraExtras.altEtekDilim.retail),
            zebraAltEtekDilimWholesale: Number(zebraExtras.altEtekDilim.wholesale),
            zebraKapaliKasaCost: Number(zebraExtras.kapaliKasa.cost),
            zebraKapaliKasaPrice: Number(zebraExtras.kapaliKasa.retail),
            zebraKapaliKasaWholesale: Number(zebraExtras.kapaliKasa.wholesale)
          };
        } else if (activeTypeTab === 'dikeyPerde') {
          updateData = {
            ...updateData,
            dikeyFonCost: Number(dikeyExtras.fon.cost),
            dikeyFonPrice: Number(dikeyExtras.fon.retail),
            dikeyFonWholesale: Number(dikeyExtras.fon.wholesale),
            dikeyFonList: dikeyExtras.fonList || []
          };
        } else if (activeTypeTab === 'katlamaliPerde') {
          updateData = {
            ...updateData,
            katlamaliMekanizmaList: katlamaliExtras.mekanizmaList || []
          };
        }
      }

      await setDoc(settingsRef, updateData, { merge: true });
      if (!overrideData) {
        setExcelModal({ show: true, type: 'success', title: t('common.success'), message: t('products.modal.settingsSavedSuccess') });
      }
    } catch (err) {
      console.error('Settings save error:', err);
      if (!overrideData) {
        setExcelModal({ show: true, type: 'error', title: t('common.error'), message: t('products.modal.settingsSaveError') });
      }
    }
    setIsSavingSettings(false);
  };

  const renderMotorSettings = (type) => {
    const settings = motorSettings[type];
    if (!settings) return null;

    const updateSetting = (field, value) => {
      setMotorSettings(prev => ({
        ...prev,
        [type]: { ...prev[type], [field]: value }
      }));
    };

    return (
      <div className="settings-panel" style={{ marginTop: '20px', border: '1px solid #b04309' }}>
        <div className="settings-panel-header">
          <i className="fas fa-microchip" style={{ color: '#b04309' }}></i>
          <h4>{t('products.motorSettings', { type: PRODUCT_TYPES[type]?.label || type, currency: settings.currency })}</h4>
        </div>
        <div className="settings-grid">
          <div className="price-group-wrapper">
            <span className="price-group-name">
              <i className="fas fa-tag" style={{ fontSize: '0.8rem' }}></i>
              {t('products.unitPrices')}
            </span>
            <div className="price-group-grid">
              <div className="price-field">
                <label>{t('products.prices.cost')}</label>
                <input type="number" className="modern-settings-input" value={settings.cost} onChange={(e) => updateSetting('cost', e.target.value)} placeholder="0.00" />
              </div>
              <div className="price-field">
                <label>{t('products.prices.retail')}</label>
                <input type="number" className="modern-settings-input" value={settings.retail} onChange={(e) => updateSetting('retail', e.target.value)} placeholder="0.00" />
              </div>
              <div className="price-field">
                <label>{t('products.prices.wholesale')}</label>
                <input type="number" className="modern-settings-input" value={settings.wholesale} onChange={(e) => updateSetting('wholesale', e.target.value)} placeholder="0.00" />
              </div>
            </div>
          </div>

          <div className="price-group-wrapper">
            <span className="price-group-name">
              <i className="fas fa-coins" style={{ fontSize: '0.8rem' }}></i>
              {t('products.currencySettings')}
            </span>
            <div className="price-group-grid">
              <div className="price-field">
                <label>{t('products.currency')}</label>
                <select className="modern-settings-input" value={settings.currency} onChange={(e) => updateSetting('currency', e.target.value)} style={{ padding: '0 8px', width: '100%' }}>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="KZT">{CURRENCY_CODE}</option>
                </select>
              </div>
              <div className="price-field">
                <label>{t('products.rateRetail')}</label>
                <input type="number" className="modern-settings-input" value={settings.specialRateRetail} onChange={(e) => updateSetting('specialRateRetail', e.target.value)} placeholder={t('products.rateAuto')} />
              </div>
              <div className="price-field">
                <label>{t('products.rateWholesale')}</label>
                <input type="number" className="modern-settings-input" value={settings.specialRateWholesale} onChange={(e) => updateSetting('specialRateWholesale', e.target.value)} placeholder={t('products.rateAuto')} />
              </div>
            </div>
          </div>

          <button
            className="btn-save"
            onClick={() => handleSaveMotorSettings(type)}
            disabled={isSavingSettings}
            style={{ backgroundColor: '#b04309', borderRadius: '10px' }}
          >
            {isSavingSettings ? '...' : t('products.saveMotorSettings', { type: PRODUCT_TYPES[type]?.label || type })}
          </button>
        </div>
      </div>
    );
  };

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    productType: Object.keys(BASE_PRODUCT_TYPES)[0],
    unitPrice: '',
    wholesalePrice: '',
    cost: '',
    // Jaluziler için ekstra fiyatlandırma
    merdivenIpliFiyat: '',
    merdivenIpliMaliyet: '',
    merdivenIpliToptan: '',
    kurdeleliFiyat: '',
    kurdeleliMaliyet: '',
    kurdeleliToptan: '',
    merdivenIpliReduktorluFiyat: '',
    merdivenIpliReduktorluMaliyet: '',
    merdivenIpliReduktorluToptan: '',
    kurdelaliReduktorluFiyat: '',
    kurdelaliReduktorluMaliyet: '',
    kurdelaliReduktorluToptan: '',
    stock: ''
  });
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Silme modal
  const [deleteModal, setDeleteModal] = useState(null);
  const [excelModal, setExcelModal] = useState({ show: false, type: '', title: '', message: '', onConfirm: null });

  // Ürünleri Firestore'dan canlı dinle (stok siparişlerden anlık güncellenir)
  useEffect(() => {
    setIsLoading(true);
    const unsubscribe = onSnapshot(
      collection(db, 'productItems'),
      (snapshot) => {
        const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        data.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'));
        setProducts(data);
        setIsLoading(false);
      },
      (error) => {
        console.error('Ürünler dinlenirken hata:', error);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Sekme değişince arama kutusunu sıfırla
  const handleTabChange = (key) => {
    React.startTransition(() => {
      setActiveTypeTab(key);
      setSearchQuery('');
    });
  };

  // Ürün sayılarını memoize et (Performans için)
  const productCounts = useMemo(() => {
    const counts = {};
    products.forEach(p => {
      counts[p.productType] = (counts[p.productType] || 0) + 1;
    });
    return counts;
  }, [products]);

  // Aktif sekmedeki ürünler (arama filtreli - Memoize edildi)
  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      if (p.productType !== activeTypeTab) return false;
      if (!searchQuery.trim()) return true;
      return (p.name || '').toLowerCase().includes(searchQuery.toLowerCase().trim());
    });
  }, [products, activeTypeTab, searchQuery]);

  const lowStockProducts = useMemo(() => {
    if (!isStockTrackedType(activeTypeTab)) return [];
    return filteredProducts
      .filter((product) => isLowStock(product.stock, activeTypeTab))
      .sort((a, b) => (parseFloat(a.stock) || 0) - (parseFloat(b.stock) || 0));
  }, [filteredProducts, activeTypeTab]);

  // Form aç - yeni ürün
  const handleAddNew = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      productType: activeTypeTab,
      unitPrice: '',
      wholesalePrice: '',
      cost: '',
      merdivenIpliFiyat: '',
      merdivenIpliMaliyet: '',
      merdivenIpliToptan: '',
      kurdeleliFiyat: '',
      kurdeleliMaliyet: '',
      kurdeleliToptan: '',
      merdivenIpliReduktorluFiyat: '',
      merdivenIpliReduktorluMaliyet: '',
      merdivenIpliReduktorluToptan: '',
      kurdelaliReduktorluFiyat: '',
      kurdelaliReduktorluMaliyet: '',
      kurdelaliReduktorluToptan: '',
      stock: ''
    });
    setFormError(null);
    setFormSuccess(null);
    setShowForm(true);
  };

  // Form aç - düzenle
  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      productType: product.productType || activeTypeTab,
      unitPrice: product.unitPrice || '',
      wholesalePrice: product.wholesalePrice || '',
      cost: product.cost || '',
      merdivenIpliFiyat: product.merdivenIpliFiyat || '',
      merdivenIpliMaliyet: product.merdivenIpliMaliyet || '',
      merdivenIpliToptan: product.merdivenIpliToptan || '',
      kurdeleliFiyat: product.kurdeleliFiyat || '',
      kurdeleliMaliyet: product.kurdeleliMaliyet || '',
      kurdeleliToptan: product.kurdeleliToptan || '',
      merdivenIpliReduktorluFiyat: product.merdivenIpliReduktorluFiyat || '',
      merdivenIpliReduktorluMaliyet: product.merdivenIpliReduktorluMaliyet || '',
      merdivenIpliReduktorluToptan: product.merdivenIpliReduktorluToptan || '',
      kurdelaliReduktorluFiyat: product.kurdelaliReduktorluFiyat || '',
      kurdelaliReduktorluMaliyet: product.kurdelaliReduktorluMaliyet || '',
      kurdelaliReduktorluToptan: product.kurdelaliReduktorluToptan || '',
      stock: product.stock ?? ''
    });
    setFormError(null);
    setFormSuccess(null);
    setShowForm(true);
  };

  // Form kapat
  const handleCancel = () => {
    setShowForm(false);
    setEditingProduct(null);
    setFormError(null);
  };

  // Kaydet
  const handleSave = async (e) => {
    e.preventDefault();
    setFormError(null);
    setFormSuccess(null);

    if (!formData.name.trim()) {
      setFormError(t('products.notifications.nameRequired'));
      return;
    }

    const isJaluzi = formData.productType === 'ahsapJaluzi';
    const isRustik = formData.productType === 'rustik';

    if (!isJaluzi && !isRustik && (!formData.unitPrice || isNaN(parseFloat(formData.unitPrice)))) {
      setFormError(t('products.notifications.invalidUnitPrice'));
      return;
    }

    if (isStockTrackedType(formData.productType)) {
      const stockValue = formData.stock === '' ? 0 : parseFloat(formData.stock);
      if (Number.isNaN(stockValue) || stockValue < 0) {
        setFormError(t('products.notifications.invalidStock'));
        return;
      }
    }

    setIsSaving(true);
    try {
      let payload = {
        name: formData.name.trim(),
        productType: formData.productType,
        updatedAt: new Date()
      };

      if (isJaluzi) {
        payload = {
          ...payload,
          priceCurrency: PRODUCT_PRICE_CURRENCY_USD,
          merdivenIpliFiyat: formData.merdivenIpliFiyat ? parseFloat(formData.merdivenIpliFiyat) : 0,
          merdivenIpliMaliyet: formData.merdivenIpliMaliyet ? parseFloat(formData.merdivenIpliMaliyet) : 0,
          merdivenIpliToptan: formData.merdivenIpliToptan ? parseFloat(formData.merdivenIpliToptan) : 0,
          kurdeleliFiyat: formData.kurdeleliFiyat ? parseFloat(formData.kurdeleliFiyat) : 0,
          kurdeleliMaliyet: formData.kurdeleliMaliyet ? parseFloat(formData.kurdeleliMaliyet) : 0,
          kurdeleliToptan: formData.kurdeleliToptan ? parseFloat(formData.kurdeleliToptan) : 0,
          merdivenIpliReduktorluFiyat: formData.merdivenIpliReduktorluFiyat ? parseFloat(formData.merdivenIpliReduktorluFiyat) : 0,
          merdivenIpliReduktorluMaliyet: formData.merdivenIpliReduktorluMaliyet ? parseFloat(formData.merdivenIpliReduktorluMaliyet) : 0,
          merdivenIpliReduktorluToptan: formData.merdivenIpliReduktorluToptan ? parseFloat(formData.merdivenIpliReduktorluToptan) : 0,
          kurdelaliReduktorluFiyat: formData.kurdelaliReduktorluFiyat ? parseFloat(formData.kurdelaliReduktorluFiyat) : 0,
          kurdelaliReduktorluMaliyet: formData.kurdelaliReduktorluMaliyet ? parseFloat(formData.kurdelaliReduktorluMaliyet) : 0,
          kurdelaliReduktorluToptan: formData.kurdelaliReduktorluToptan ? parseFloat(formData.kurdelaliReduktorluToptan) : 0
        };
      } else if (isRustik) {
        payload = {
          ...payload,
          unitPrice: 0,
          cost: 0,
          stock: formData.stock === '' ? 0 : parseFloat(formData.stock) || 0
        };
      } else {
        payload = {
          ...payload,
          priceCurrency: PRODUCT_PRICE_CURRENCY_USD,
          unitPrice: parseFloat(formData.unitPrice),
          wholesalePrice: formData.wholesalePrice ? parseFloat(formData.wholesalePrice) : 0,
          cost: formData.cost ? parseFloat(formData.cost) : 0
        };
        if (isStockTrackedType(formData.productType)) {
          payload.stock = formData.stock === '' ? 0 : parseFloat(formData.stock) || 0;
        }
      }

      if (editingProduct) {
        await updateDoc(doc(db, 'productItems', editingProduct.id), payload);
        setFormSuccess(t('products.notifications.updatedSuccess'));
      } else {
        await addDoc(collection(db, 'productItems'), {
          ...payload,
          createdAt: new Date()
        });
        setFormSuccess(t('products.notifications.addedSuccess'));
      }

      setActiveTypeTab(formData.productType);

      setTimeout(() => {
        setShowForm(false);
        setEditingProduct(null);
        setFormSuccess(null);
      }, 1200);
    } catch (error) {
      console.error('Ürün kaydedilirken hata:', error);
      setFormError(t('products.notifications.saveError', { message: error.message }));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal) return;
    try {
      await deleteDoc(doc(db, 'productItems', deleteModal.id));
      setDeleteModal(null);
    } catch (error) {
      console.error('Ürün silinirken hata:', error);
    }
  };

  const handleExportExcel = () => {
    if (filteredProducts.length === 0) {
      setExcelModal({ show: true, type: 'info', title: t('common.info'), message: t('products.modal.noExportProducts') });
      return;
    }

    const isJaluzi = activeTypeTab === 'ahsapJaluzi';
    const exportData = filteredProducts.map(p => buildExcelExportRow(p, isJaluzi, t, activeTypeTab));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, t('products.excel.sheetName'));

    const typeLabel = PRODUCT_TYPES[activeTypeTab]?.label || t('products.excel.sheetName');
    XLSX.writeFile(workbook, `${typeLabel}_${t('products.excel.fileNameSuffix')}.xlsx`);
  };

  const handleImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleClearList = () => {
    const typeLabel = PRODUCT_TYPES[activeTypeTab]?.label;
    const currentCount = products.filter(p => p.productType === activeTypeTab).length;
    if (currentCount === 0) {
      setExcelModal({ show: true, type: 'info', title: t('common.info'), message: t('products.modal.noProductsToDelete') });
      return;
    }
    setExcelModal({
      show: true,
      type: 'confirm',
      title: t('products.clearList'),
      message: t('products.modal.clearListConfirm', { category: typeLabel, count: currentCount }),
      onConfirm: async () => {
        setExcelModal({ show: false });
        setIsExcelLoading(true);
        try {
          const toDelete = products.filter(p => p.productType === activeTypeTab);
          const chunkSize = 400;
          for (let i = 0; i < toDelete.length; i += chunkSize) {
            const chunk = toDelete.slice(i, i + chunkSize);
            const batch = writeBatch(db);
            chunk.forEach(p => batch.delete(doc(db, 'productItems', p.id)));
            await batch.commit();
          }
          setExcelModal({ show: true, type: 'success', title: t('products.modal.clearedTitle'), message: t('products.modal.productsDeletedSuccess', { count: toDelete.length }) });
        } catch (err) {
          setExcelModal({ show: true, type: 'error', title: t('common.error'), message: t('products.modal.clearListError', { message: err.message }) });
        } finally {
          setIsExcelLoading(false);
        }
      }
    });
  };

  const processExcelData = async (data, typeLabel) => {
    setExcelModal({ show: false });
    try {
      const isJaluzi = activeTypeTab === 'ahsapJaluzi';
      const existingByName = {};
      products
        .filter(p => p.productType === activeTypeTab)
        .forEach(p => {
          existingByName[(p.name || '').toLowerCase().trim()] = p;
        });

      const chunks = [];
      let currentChunk = [];

      for (const row of data) {
        const name = getExcelRowValue(row, 'productName', t)?.toString().trim();
        if (!name) continue;

        let payload = {
          name,
          productType: activeTypeTab,
          updatedAt: new Date()
        };

        let isValid = false;

        if (isJaluzi) {
          payload = {
            ...payload,
            priceCurrency: PRODUCT_PRICE_CURRENCY_USD,
            merdivenIpliFiyat: parseFloat(getExcelRowValue(row, 'merdivenIpli', t)) || 0,
            merdivenIpliMaliyet: parseFloat(getExcelRowValue(row, 'merdivenIpliCost', t)) || 0,
            merdivenIpliToptan: parseFloat(getExcelRowValue(row, 'merdivenIpliWholesale', t)) || 0,
            kurdeleliFiyat: parseFloat(getExcelRowValue(row, 'kurdeleli', t)) || 0,
            kurdeleliMaliyet: parseFloat(getExcelRowValue(row, 'kurdeleliCost', t)) || 0,
            kurdeleliToptan: parseFloat(getExcelRowValue(row, 'kurdeleliWholesale', t)) || 0,
            merdivenIpliReduktorluFiyat: parseFloat(getExcelRowValue(row, 'merdivenIpliRed', t)) || 0,
            merdivenIpliReduktorluMaliyet: parseFloat(getExcelRowValue(row, 'merdivenIpliRedCost', t)) || 0,
            merdivenIpliReduktorluToptan: parseFloat(getExcelRowValue(row, 'merdivenIpliRedWholesale', t)) || 0,
            kurdelaliReduktorluFiyat: parseFloat(getExcelRowValue(row, 'kurdeleliRed', t)) || 0,
            kurdelaliReduktorluMaliyet: parseFloat(getExcelRowValue(row, 'kurdeleliRedCost', t)) || 0,
            kurdelaliReduktorluToptan: parseFloat(getExcelRowValue(row, 'kurdeleliRedWholesale', t)) || 0
          };
          isValid = true;
        } else if (activeTypeTab === 'rustik') {
          const stock = parseFloat(getExcelRowValue(row, 'stock', t));
          payload = {
            ...payload,
            unitPrice: 0,
            cost: 0,
            stock: Number.isNaN(stock) ? 0 : stock
          };
          isValid = true;
        } else {
          const unitPrice = parseFloat(getExcelRowValue(row, 'unitPrice', t));
          const wholesalePrice = parseFloat(getExcelRowValue(row, 'wholesalePrice', t));
          const cost = parseFloat(getExcelRowValue(row, 'cost', t));
          if (!isNaN(unitPrice)) {
            payload = {
              ...payload,
              ...(activeTypeTab !== 'rustik' ? { priceCurrency: PRODUCT_PRICE_CURRENCY_USD } : {}),
              unitPrice,
              wholesalePrice: isNaN(wholesalePrice) ? 0 : wholesalePrice,
              cost: isNaN(cost) ? 0 : cost
            };
            if (isStockTrackedType(activeTypeTab)) {
              const stock = parseFloat(getExcelRowValue(row, 'stock', t));
              payload.stock = Number.isNaN(stock) ? 0 : stock;
            }
            isValid = true;
          }
        }

        if (!isValid) continue;

        const existing = existingByName[name.toLowerCase()];
        currentChunk.push({ existing, payload });

        if (currentChunk.length === 400) {
          chunks.push(currentChunk);
          currentChunk = [];
        }
      }

      if (currentChunk.length > 0) {
        chunks.push(currentChunk);
      }

      let updateCount = 0;
      let addCount = 0;

      for (const chunk of chunks) {
        const batch = writeBatch(db);
        for (const { existing, payload } of chunk) {
          if (existing) {
            batch.update(doc(db, 'productItems', existing.id), payload);
            updateCount++;
          } else {
            const newRef = doc(collection(db, 'productItems'));
            batch.set(newRef, { ...payload, createdAt: new Date() });
            addCount++;
          }
        }
        await batch.commit();
      }

      const msg = [
        updateCount > 0 ? t('products.modal.excelProductsUpdated', { count: updateCount }) : null,
        addCount > 0 ? t('products.modal.excelProductsAdded', { count: addCount }) : null
      ].filter(Boolean).join(', ');

      setExcelModal({
        show: true,
        type: 'success',
        title: t('common.success'),
        message: msg ? t('products.modal.excelImportSuccess', { details: msg }) : t('products.modal.excelNoChanges')
      });
    } catch (error) {
      console.error('Excel içe aktarılırken hata:', error);
      setExcelModal({ show: true, type: 'error', title: t('common.error'), message: t('products.modal.excelReadError', { message: error.message }) });
    } finally {
      setIsExcelLoading(false);
    }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = null;
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        setIsExcelLoading(true);
        const bstr = evt.target.result;
        const workbook = XLSX.read(bstr, { type: 'binary' });
        const worksheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[worksheetName];
        const data = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

        if (data.length === 0) {
          setExcelModal({ show: true, type: 'error', title: t('common.error'), message: t('products.modal.excelEmpty') });
          setIsExcelLoading(false);
          return;
        }

        const firstRow = data[0];
        const isJaluzi = activeTypeTab === 'ahsapJaluzi';
        const formatParams = getExcelFormatErrorParams(t);

        if (isJaluzi) {
          if (!hasExcelColumn(firstRow, 'productName', t) || !hasExcelColumn(firstRow, 'merdivenIpli', t)) {
            setExcelModal({ show: true, type: 'error', title: t('products.modal.invalidFormatTitle'), message: t('products.modal.invalidFormatJaluzi', formatParams) });
            setIsExcelLoading(false);
            return;
          }
        } else {
          if (!hasExcelColumn(firstRow, 'productName', t) || !hasExcelColumn(firstRow, 'unitPrice', t)) {
            setExcelModal({ show: true, type: 'error', title: t('products.modal.invalidFormatTitle'), message: t('products.modal.invalidFormatDefault', formatParams) });
            setIsExcelLoading(false);
            return;
          }
        }

        const typeLabel = PRODUCT_TYPES[activeTypeTab]?.label;
        const existingCount = products.filter(p => p.productType === activeTypeTab).length;
        setExcelModal({
          show: true,
          type: 'confirm',
          title: t('products.modal.excelImportConfirmTitle'),
          message: t('products.modal.excelImportConfirm', { count: data.length, existing: existingCount }),
          onConfirm: () => processExcelData(data, typeLabel)
        });
      } catch (error) {
        console.error('Excel dosyası parse edilirken hata:', error);
        setExcelModal({ show: true, type: 'error', title: t('products.modal.parseErrorTitle'), message: t('products.modal.parseError') });
        setIsExcelLoading(false);
      }
    };
    reader.readAsBinaryString(file);
  };

  return (
    <div className="product-list-page">
      <div className="product-list-header">
        <div className="product-list-title">
          <h2>{t('products.title')}</h2>
          <p>{t('products.description')}</p>
        </div>
      </div>

      <div className={`global-topbar${settingsReadOnly ? ' topbar-readonly-rates' : ''}`}>
        <div className="topbar-title">
          <i className="fas fa-coins" style={{ color: '#b04309' }}></i>
          <h4>{t('products.exchangeRates')}</h4>
        </div>
        <div className="topbar-exchange-rates">
          {['USD', 'EUR'].map((currency) => (
            <div
              key={currency}
              className={`topbar-rate topbar-rate-editable${manualOverrides[currency] != null ? ' is-manual' : ''}`}
              title={currency === 'USD' ? t('products.usdRate') : t('products.eurRate')}
            >
              <i className={`fas fa-${currency === 'USD' ? 'dollar-sign' : 'euro-sign'}`}></i>
              <input
                type="number"
                step="0.01"
                min="0"
                className="topbar-rate-input"
                value={rateDrafts[currency]}
                onChange={(e) => handleRateDraftChange(currency, e.target.value)}
                onBlur={() => commitManualRate(currency)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.currentTarget.blur();
                  }
                }}
                disabled={settingsReadOnly}
                readOnly={settingsReadOnly}
                aria-label={currency === 'USD' ? t('products.usdRate') : t('products.eurRate')}
              />
              {!settingsReadOnly && manualOverrides[currency] != null && (
                <button
                  type="button"
                  className="topbar-rate-reset"
                  onClick={() => resetManualRate(currency)}
                  title={t('products.resetToAutoRate')}
                >
                  <i className="fas fa-undo"></i>
                </button>
              )}
              {!settingsReadOnly && manualOverrides[currency] != null && (
                <span className="topbar-rate-manual-badge">{t('products.manualRate')}</span>
              )}
            </div>
          ))}
          <button
            type="button"
            className="topbar-rate-refresh"
            onClick={handleRefreshExchangeRates}
            disabled={isRefreshingRates}
            title={t('products.refreshRates')}
          >
            <i className={`fas fa-sync-alt${isRefreshingRates ? ' fa-spin' : ''}`}></i>
            {isRefreshingRates ? t('products.refreshingRates') : t('products.refreshRates')}
          </button>
        </div>
      </div>

      <div className="product-nav-container">
        {Object.entries(PRODUCT_TYPES)
          .map(([key, type]) => {
            const count = productCounts[key] || 0;
            return (
              <button
                key={key}
                className={`nav-pill ${activeTypeTab === key ? 'active' : ''}`}
                onClick={() => handleTabChange(key)}
              >
                <i className={`fas fa-${key === 'tul' ? 'scroll' :
                    key === 'kanat' ? 'border-all' :
                      key === 'ahsapJaluzi' ? 'align-justify' :
                        key === 'metalJaluzi' ? 'th-list' :
                          key === 'stor' ? 'window-maximize' :
                            key === 'zebra' ? 'equals' :
                              key === 'dikeyPerde' ? 'columns' :
                                key === 'plise' ? 'layer-group' :
                                  key === 'raySistemi' ? 'road' :
                                    key === 'rustik' ? 'ellipsis-h' : 'box'
                  }`}></i>
                {type.label}
                <span className={`tab-count ${isLoading ? 'loading' : ''} ${!isLoading && count === 0 ? 'empty' : ''}`}>
                  {isLoading ? '...' : count}
                </span>
              </button>
            );
          })}
      </div>

      <div className="product-table-wrapper">
        <div className="product-table-header-bar">
          <div className="product-table-title">
            <i className="fas fa-list"></i>
            <span>{t('products.categoryProducts', { category: getCategoryLabel(activeTypeTab) })}</span>
            <span className="product-count">{t('products.productCount', { count: filteredProducts.length })}</span>
          </div>
          {(canManageProducts) && (
            <div className="excel-actions">
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept=".xlsx, .xls, .csv"
                onChange={handleImportExcel}
              />
              <button
                className="btn-excel btn-import"
                onClick={handleImportClick}
                disabled={isExcelLoading}
              >
                {isExcelLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-file-import"></i>} {t('products.importExcel')}
              </button>
              <button
                className="btn-excel btn-export"
                onClick={handleExportExcel}
                disabled={filteredProducts.length === 0 || isExcelLoading}
              >
                <i className="fas fa-file-export"></i> {t('products.exportExcel')}
              </button>
              <button
                className="btn-excel btn-clear-list"
                onClick={handleClearList}
                disabled={isExcelLoading}
                title={t('products.clearListTitle')}
              >
                <i className="fas fa-trash-alt"></i> {t('products.clearList')}
              </button>
            </div>
          )}
        </div>

        {lowStockProducts.length > 0 && (
          <div className="stock-low-warning" role="alert">
            <div className="stock-low-warning-header">
              <i className="fas fa-exclamation-triangle"></i>
              <strong>{t('products.stock.lowStockTitle')}</strong>
              <span>
                {t('products.stock.lowStockSummary', {
                  count: lowStockProducts.length,
                  threshold: usesMeterStock(activeTypeTab) ? STOCK_LOW_THRESHOLD_METERS : STOCK_LOW_THRESHOLD_PIECES,
                  unit: usesMeterStock(activeTypeTab) ? t('products.stock.unitMeterShort') : t('products.stock.unitPieceShort')
                })}
              </span>
            </div>
            <ul className="stock-low-warning-list">
              {lowStockProducts.map((product) => (
                <li key={product.id}>
                  <span className="stock-low-warning-name">{product.name}</span>
                  <span className="stock-low-warning-value">
                    {formatStockDisplay(product.stock, product.productType, t, formatPrice)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className={`product-settings-container${settingsReadOnly ? ' settings-readonly' : ''}`}>
          {activeTypeTab === 'rustik' && canViewProductSettings && (
            <div className="settings-card">
              <div
                className="settings-header clickable"
                onClick={() => setIsRustikSettingsCollapsed(!isRustikSettingsCollapsed)}
              >
                <div className="settings-title">
                  <i className="fas fa-ellipsis-h"></i>
                  <span>{t('products.settings.rustikLegFees')}</span>
                </div>
                <i className={`fas fa-chevron-${isRustikSettingsCollapsed ? 'down' : 'up'}`}></i>
              </div>

              {!isRustikSettingsCollapsed && (
                <div className="settings-content">
                  <div className="settings-grid">
                    <div className="settings-column">
                      <div
                        className="settings-sub-header clickable"
                        onClick={() => setIsRustikModelCollapsed(!isRustikModelCollapsed)}
                      >
                        <h4><i className="fas fa-couch"></i> {t('products.settings.rustik.legModelList')}</h4>
                        <i className={`fas fa-chevron-${isRustikModelCollapsed ? 'down' : 'up'}`}></i>
                      </div>

                      {!isRustikModelCollapsed && (
                        <div className="settings-sub-content">
                          <div className="list-add-form">
                            <input
                              type="text"
                              placeholder={t('products.settings.rustik.newModel')}
                              value={newRustikModel}
                              onChange={(e) => setNewRustikModel(e.target.value)}
                            />
                            <button onClick={() => {
                              if (!newRustikModel) return;
                              const newList = [...rustikExtras.ayakModelList, newRustikModel];
                              setRustikExtras({ ...rustikExtras, ayakModelList: newList });
                              setNewRustikModel('');
                              handleSaveSettings({ rustikAyakModelList: newList });
                            }}><i className="fas fa-plus"></i></button>
                          </div>
                          <div className="settings-list-container">
                            {rustikExtras.ayakModelList.length === 0 && <span className="empty-list-msg">{t('products.settings.rustik.emptyList')}</span>}
                            <ul className="settings-inner-list">
                              {rustikExtras.ayakModelList.map((item, index) => (
                                <li key={index} className="settings-list-item">
                                  {editingRustikModel.index === index ? (
                                    <div className="edit-mode-container">
                                      <input
                                        type="text"
                                        value={editingRustikModel.value}
                                        onChange={(e) => setEditingRustikModel({ ...editingRustikModel, value: e.target.value })}
                                        autoFocus
                                      />
                                      <div className="edit-actions">
                                        <button className="btn-confirm" onClick={() => {
                                          const newList = [...rustikExtras.ayakModelList];
                                          newList[index] = editingRustikModel.value;
                                          setRustikExtras({ ...rustikExtras, ayakModelList: newList });
                                          handleSaveSettings({ rustikAyakModelList: newList });
                                          setEditingRustikModel({ index: null, value: '' });
                                        }}><i className="fas fa-check"></i></button>
                                        <button className="btn-cancel" onClick={() => setEditingRustikModel({ index: null, value: '' })}><i className="fas fa-times"></i></button>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <span className="item-text">{item}</span>
                                      <div className="item-actions">
                                        <button className="btn-edit" onClick={() => setEditingRustikModel({ index, value: item })}><i className="fas fa-edit"></i></button>
                                        <button className="btn-delete" onClick={() => {
                                          const newList = rustikExtras.ayakModelList.filter((_, i) => i !== index);
                                          setRustikExtras({ ...rustikExtras, ayakModelList: newList });
                                          handleSaveSettings({ rustikAyakModelList: newList });
                                        }}><i className="fas fa-trash-alt"></i></button>
                                      </div>
                                    </>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="settings-column">
                      <div
                        className="settings-sub-header clickable"
                        onClick={() => setIsRustikCapCollapsed(!isRustikCapCollapsed)}
                      >
                        <h4><i className="fas fa-ruler-combined"></i> {t('products.settings.rustik.legDiameterList')}</h4>
                        <i className={`fas fa-chevron-${isRustikCapCollapsed ? 'down' : 'up'}`}></i>
                      </div>

                      {!isRustikCapCollapsed && (
                        <div className="settings-sub-content">
                          <div className="list-add-form">
                            <input
                              type="text"
                              placeholder={t('products.settings.rustik.newDiameter')}
                              value={newRustikCap}
                              onChange={(e) => setNewRustikCap(e.target.value)}
                            />
                            <button onClick={() => {
                              if (!newRustikCap) return;
                              const newList = [...rustikExtras.ayakCapList, newRustikCap];
                              setRustikExtras({ ...rustikExtras, ayakCapList: newList });
                              setNewRustikCap('');
                              handleSaveSettings({ rustikAyakCapList: newList });
                            }}><i className="fas fa-plus"></i></button>
                          </div>
                          <div className="settings-list-container">
                            {rustikExtras.ayakCapList.length === 0 && <span className="empty-list-msg">{t('products.settings.rustik.emptyList')}</span>}
                            <ul className="settings-inner-list">
                              {rustikExtras.ayakCapList.map((item, index) => (
                                <li key={index} className="settings-list-item">
                                  {editingRustikCap.index === index ? (
                                    <div className="edit-mode-container">
                                      <input
                                        type="text"
                                        value={editingRustikCap.value}
                                        onChange={(e) => setEditingRustikCap({ ...editingRustikCap, value: e.target.value })}
                                        autoFocus
                                      />
                                      <div className="edit-actions">
                                        <button className="btn-confirm" onClick={() => {
                                          const newList = [...rustikExtras.ayakCapList];
                                          newList[index] = editingRustikCap.value;
                                          setRustikExtras({ ...rustikExtras, ayakCapList: newList });
                                          handleSaveSettings({ rustikAyakCapList: newList });
                                          setEditingRustikCap({ index: null, value: '' });
                                        }}><i className="fas fa-check"></i></button>
                                        <button className="btn-cancel" onClick={() => setEditingRustikCap({ index: null, value: '' })}><i className="fas fa-times"></i></button>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <span className="item-text">{item}</span>
                                      <div className="item-actions">
                                        <button className="btn-edit" onClick={() => setEditingRustikCap({ index, value: item })}><i className="fas fa-edit"></i></button>
                                        <button className="btn-delete" onClick={() => {
                                          const newList = rustikExtras.ayakCapList.filter((_, i) => i !== index);
                                          setRustikExtras({ ...rustikExtras, ayakCapList: newList });
                                          handleSaveSettings({ rustikAyakCapList: newList });
                                        }}><i className="fas fa-trash-alt"></i></button>
                                      </div>
                                    </>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="settings-column">
                      <div
                        className="settings-sub-header clickable"
                        onClick={() => setIsRustikUzunlukCollapsed(!isRustikUzunlukCollapsed)}
                      >
                        <h4><i className="fas fa-arrows-alt-v"></i> {t('products.settings.rustik.legLength')}</h4>
                        <i className={`fas fa-chevron-${isRustikUzunlukCollapsed ? 'down' : 'up'}`}></i>
                      </div>

                      {!isRustikUzunlukCollapsed && (
                        <div className="settings-sub-content">
                          <div className="list-add-form">
                            <input
                              type="text"
                              placeholder={t('products.settings.rustik.newLength')}
                              value={newRustikUzunluk}
                              onChange={(e) => setNewRustikUzunluk(e.target.value)}
                            />
                            <button onClick={() => {
                              if (!newRustikUzunluk) return;
                              const newList = [...rustikExtras.ayakUzunlukList, newRustikUzunluk];
                              setRustikExtras({ ...rustikExtras, ayakUzunlukList: newList });
                              setNewRustikUzunluk('');
                              handleSaveSettings({ rustikAyakUzunlukList: newList });
                            }}><i className="fas fa-plus"></i></button>
                          </div>
                          <div className="settings-list-container">
                            {rustikExtras.ayakUzunlukList.length === 0 && <span className="empty-list-msg">{t('products.settings.rustik.emptyList')}</span>}
                            <ul className="settings-inner-list">
                              {rustikExtras.ayakUzunlukList.map((item, index) => (
                                <li key={index} className="settings-list-item">
                                  {editingRustikUzunluk.index === index ? (
                                    <div className="edit-mode-container">
                                      <input
                                        type="text"
                                        value={editingRustikUzunluk.value}
                                        onChange={(e) => setEditingRustikUzunluk({ ...editingRustikUzunluk, value: e.target.value })}
                                        autoFocus
                                      />
                                      <div className="edit-actions">
                                        <button className="btn-confirm" onClick={() => {
                                          const newList = [...rustikExtras.ayakUzunlukList];
                                          newList[index] = editingRustikUzunluk.value;
                                          setRustikExtras({ ...rustikExtras, ayakUzunlukList: newList });
                                          handleSaveSettings({ rustikAyakUzunlukList: newList });
                                          setEditingRustikUzunluk({ index: null, value: '' });
                                        }}><i className="fas fa-check"></i></button>
                                        <button className="btn-cancel" onClick={() => setEditingRustikUzunluk({ index: null, value: '' })}><i className="fas fa-times"></i></button>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <span className="item-text">{item}</span>
                                      <div className="item-actions">
                                        <button className="btn-edit" onClick={() => setEditingRustikUzunluk({ index, value: item })}><i className="fas fa-edit"></i></button>
                                        <button className="btn-delete" onClick={() => {
                                          const newList = rustikExtras.ayakUzunlukList.filter((_, i) => i !== index);
                                          setRustikExtras({ ...rustikExtras, ayakUzunlukList: newList });
                                          handleSaveSettings({ rustikAyakUzunlukList: newList });
                                        }}><i className="fas fa-trash-alt"></i></button>
                                      </div>
                                    </>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="settings-column">
                      <div
                        className="settings-sub-header clickable"
                        onClick={() => setIsRustikRenkCollapsed(!isRustikRenkCollapsed)}
                      >
                        <h4><i className="fas fa-palette"></i> {t('products.settings.rustik.colorCodeList')}</h4>
                        <i className={`fas fa-chevron-${isRustikRenkCollapsed ? 'down' : 'up'}`}></i>
                      </div>

                      {!isRustikRenkCollapsed && (
                        <div className="settings-sub-content">
                          <div className="list-add-form">
                            <input
                              type="text"
                              placeholder={t('products.settings.rustik.newColor')}
                              value={newRustikRenk}
                              onChange={(e) => setNewRustikRenk(e.target.value)}
                            />
                            <button onClick={() => {
                              if (!newRustikRenk) return;
                              const newList = [...rustikExtras.ayakRenkKoduList, newRustikRenk];
                              setRustikExtras({ ...rustikExtras, ayakRenkKoduList: newList });
                              setNewRustikRenk('');
                              handleSaveSettings({ rustikAyakRenkKoduList: newList });
                            }}><i className="fas fa-plus"></i></button>
                          </div>
                          <div className="settings-list-container">
                            {rustikExtras.ayakRenkKoduList.length === 0 && <span className="empty-list-msg">{t('products.settings.rustik.emptyList')}</span>}
                            <ul className="settings-inner-list">
                              {rustikExtras.ayakRenkKoduList.map((item, index) => (
                                <li key={index} className="settings-list-item">
                                  {editingRustikRenk.index === index ? (
                                    <div className="edit-mode-container">
                                      <input
                                        type="text"
                                        value={editingRustikRenk.value}
                                        onChange={(e) => setEditingRustikRenk({ ...editingRustikRenk, value: e.target.value })}
                                        autoFocus
                                      />
                                      <div className="edit-actions">
                                        <button className="btn-confirm" onClick={() => {
                                          const newList = [...rustikExtras.ayakRenkKoduList];
                                          newList[index] = editingRustikRenk.value;
                                          setRustikExtras({ ...rustikExtras, ayakRenkKoduList: newList });
                                          handleSaveSettings({ rustikAyakRenkKoduList: newList });
                                          setEditingRustikRenk({ index: null, value: '' });
                                        }}><i className="fas fa-check"></i></button>
                                        <button className="btn-cancel" onClick={() => setEditingRustikRenk({ index: null, value: '' })}><i className="fas fa-times"></i></button>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <span className="item-text">{item}</span>
                                      <div className="item-actions">
                                        <button className="btn-edit" onClick={() => setEditingRustikRenk({ index, value: item })}><i className="fas fa-edit"></i></button>
                                        <button className="btn-delete" onClick={() => {
                                          const newList = rustikExtras.ayakRenkKoduList.filter((_, i) => i !== index);
                                          setRustikExtras({ ...rustikExtras, ayakRenkKoduList: newList });
                                          handleSaveSettings({ rustikAyakRenkKoduList: newList });
                                        }}><i className="fas fa-trash-alt"></i></button>
                                      </div>
                                    </>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Diğer Ürün Tiplerinin Ayarları */}
          {canViewProductSettings && (
            <>
          {(activeTypeTab === 'tul' || activeTypeTab === 'kanat') && (
            <PileSettingsPanel
              extraPrices={extraPrices}
              setExtraPrices={setExtraPrices}
              isSavingSettings={isSavingSettings}
              handleSaveSettings={handleSaveSettings}
            />
          )}

          {activeTypeTab === 'ahsapJaluzi' && (
            <>
              <div className="settings-panel">
                <div className="settings-panel-header">
                  <i className="fas fa-layer-group"></i>
                  <h4>{t('products.settings.woodJaluziFees')}</h4>
                </div>
                <div className="settings-grid">
                  <SettingsPriceGroup
                    groupName={t('products.settings.groups.zigilSystem')}
                    iconClass="fas fa-link"
                    values={ahsapJaluziExtras.zigil}
                    onFieldChange={(field, value) => setAhsapJaluziExtras(p => ({ ...p, zigil: { ...p.zigil, [field]: value } }))}
                  />
                  <button className="btn-save" onClick={() => handleSaveSettings()} disabled={isSavingSettings} style={{ borderRadius: '10px' }}>
                    {isSavingSettings ? '...' : t('common.save')}
                  </button>
                </div>
              </div>
              {renderMotorSettings('ahsapJaluzi')}
            </>
          )}

          {activeTypeTab === 'metalJaluzi' && (
            <>
              <div className="settings-panel">
                <div className="settings-panel-header">
                  <i className="fas fa-layer-group"></i>
                  <h4>{t('products.settings.metalJaluziFees')}</h4>
                </div>
                <div className="settings-grid">
                  <SettingsPriceGroup
                    groupName={t('products.settings.groups.zigilSystem')}
                    iconClass="fas fa-link"
                    values={metalJaluziExtras.zigil}
                    onFieldChange={(field, value) => setMetalJaluziExtras(p => ({ ...p, zigil: { ...p.zigil, [field]: value } }))}
                  />
                  <SettingsPriceGroup
                    groupName={t('products.settings.groups.reducerDiff')}
                    iconClass="fas fa-cog"
                    values={metalJaluziExtras.reduktor}
                    onFieldChange={(field, value) => setMetalJaluziExtras(p => ({ ...p, reduktor: { ...p.reduktor, [field]: value } }))}
                  />
                  <SettingsPriceGroup
                    groupName={t('products.settings.groups.betweenGlass')}
                    iconClass="fas fa-window-restore"
                    values={metalJaluziExtras.ikiCamArasi}
                    onFieldChange={(field, value) => setMetalJaluziExtras(p => ({ ...p, ikiCamArasi: { ...p.ikiCamArasi, [field]: value } }))}
                  />
                  <button className="btn-save" onClick={() => handleSaveSettings()} disabled={isSavingSettings} style={{ borderRadius: '10px' }}>
                    {isSavingSettings ? '...' : t('common.save')}
                  </button>
                </div>
              </div>
              {renderMotorSettings('metalJaluzi')}
            </>
          )}

          {activeTypeTab === 'stor' && (
            <>
              <div className="settings-panel">
                <div className="settings-panel-header">
                  <i className="fas fa-cog"></i>
                  <h4>{t('products.settings.storFees')}</h4>
                </div>
                <div className="settings-grid">
                  <SettingsPriceGroup groupName={t('products.settings.groups.jumboSystem')} iconClass="fas fa-expand" values={storExtras.jumbo} onFieldChange={(field, value) => setStorExtras(p => ({ ...p, jumbo: { ...p.jumbo, [field]: value } }))} />
                  <SettingsPriceGroup groupName={t('products.settings.groups.bottomHemSlice')} iconClass="fas fa-cut" values={storExtras.altEtekDilim} onFieldChange={(field, value) => setStorExtras(p => ({ ...p, altEtekDilim: { ...p.altEtekDilim, [field]: value } }))} />
                  <SettingsPriceGroup groupName={t('products.settings.groups.closedCase')} iconClass="fas fa-box" values={storExtras.kapaliKasa} onFieldChange={(field, value) => setStorExtras(p => ({ ...p, kapaliKasa: { ...p.kapaliKasa, [field]: value } }))} />
                  <SettingsPriceGroup groupName={t('products.settings.groups.bottomHemLaser')} iconClass="fas fa-magic" values={storExtras.altEtekLazer} onFieldChange={(field, value) => setStorExtras(p => ({ ...p, altEtekLazer: { ...p.altEtekLazer, [field]: value } }))} />
                  <SettingsPriceGroup groupName={t('products.settings.groups.decorativeSlat')} iconClass="fas fa-border-style" values={storExtras.dekoratifLama} onFieldChange={(field, value) => setStorExtras(p => ({ ...p, dekoratifLama: { ...p.dekoratifLama, [field]: value } }))} />
                  <SettingsPriceGroup groupName={t('products.settings.groups.decorativeCase')} iconClass="fas fa-vector-square" values={storExtras.dekoratifKasa} onFieldChange={(field, value) => setStorExtras(p => ({ ...p, dekoratifKasa: { ...p.dekoratifKasa, [field]: value } }))} />
                  <SettingsPriceGroup groupName={t('products.settings.groups.bottomHemWhiteWork')} iconClass="fas fa-brush" values={storExtras.altEtekBeyazIs} onFieldChange={(field, value) => setStorExtras(p => ({ ...p, altEtekBeyazIs: { ...p.altEtekBeyazIs, [field]: value } }))} />
                  <button className="btn-save" onClick={() => handleSaveSettings()} disabled={isSavingSettings} style={{ borderRadius: '10px' }}>
                    {isSavingSettings ? '...' : t('common.save')}
                  </button>
                </div>
              </div>
              {renderMotorSettings('stor')}
            </>
          )}

          {activeTypeTab === 'zebra' && (
            <>
              <div className="settings-panel">
                <div className="settings-panel-header">
                  <i className="fas fa-stream"></i>
                  <h4>{t('products.settings.zebraFees')}</h4>
                </div>
                <div className="settings-grid">
                  <SettingsPriceGroup groupName={t('products.settings.groups.jumboSystem')} iconClass="fas fa-expand" values={zebraExtras.jumbo} onFieldChange={(field, value) => setZebraExtras(p => ({ ...p, jumbo: { ...p.jumbo, [field]: value } }))} />
                  <SettingsPriceGroup groupName={t('products.settings.groups.bottomHemSlice')} iconClass="fas fa-cut" values={zebraExtras.altEtekDilim} onFieldChange={(field, value) => setZebraExtras(p => ({ ...p, altEtekDilim: { ...p.altEtekDilim, [field]: value } }))} />
                  <SettingsPriceGroup groupName={t('products.settings.groups.closedCase')} iconClass="fas fa-box" values={zebraExtras.kapaliKasa} onFieldChange={(field, value) => setZebraExtras(p => ({ ...p, kapaliKasa: { ...p.kapaliKasa, [field]: value } }))} />
                  <button className="btn-save" onClick={() => handleSaveSettings()} disabled={isSavingSettings} style={{ borderRadius: '10px' }}>
                    {isSavingSettings ? '...' : t('common.save')}
                  </button>
                </div>
              </div>
              {renderMotorSettings('zebra')}
            </>
          )}

          {activeTypeTab === 'dikeyPerde' && (
            <>
              <div className="settings-panel">
                <div className="settings-panel-header">
                  <i className="fas fa-arrows-alt-v"></i>
                  <h4>{t('products.settings.verticalCurtainLining')}</h4>
                </div>
                <div className="settings-grid">
                  <SettingsPriceGroup
                    groupName={t('products.settings.groups.verticalLiningFee')}
                    iconClass="fas fa-arrows-alt-v"
                    values={dikeyExtras.fon}
                    onFieldChange={(field, value) => setDikeyExtras(p => ({ ...p, fon: { ...p.fon, [field]: value } }))}
                  />
                  <button className="btn-save" onClick={() => handleSaveSettings()} disabled={isSavingSettings} style={{ borderRadius: '10px' }}>
                    {isSavingSettings ? '...' : t('common.save')}
                  </button>
                </div>
              </div>
              {renderMotorSettings('dikeyPerde')}
            </>
          )}

          {activeTypeTab === 'dikeyPvc' && renderMotorSettings('dikeyPvc')}
          {activeTypeTab === 'dikeyStor' && renderMotorSettings('dikeyStor')}

          {activeTypeTab === 'katlamaliPerde' && (
            <>
              <div className="settings-panel">
                <div className="settings-panel-header">
                  <i className="fas fa-tools"></i>
                  <h4>{t('products.settings.foldingMechanism')}</h4>
                </div>
                <div className="settings-grid">
                  <div style={{ width: '100%' }}>
                    <div
                      onClick={() => setIsKatlamaliMekanizmaCollapsed(!isKatlamaliMekanizmaCollapsed)}
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', marginBottom: '10px' }}
                    >
                      <h4 style={{ margin: 0, fontSize: '14px', color: '#334155', fontWeight: '600' }}>{t('products.settings.katlamali.mechanismTypesAndFees')}</h4>
                      <i className={`fas fa-chevron-${isKatlamaliMekanizmaCollapsed ? 'down' : 'up'}`} style={{ color: '#64748b', fontSize: '12px' }}></i>
                    </div>

                    {!isKatlamaliMekanizmaCollapsed && (
                      <div className="mekanizma-list-content">
                        <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', width: '100%', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                          <div className="form-group" style={{ flex: 2, minWidth: '150px', marginBottom: 0 }}>
                            <label>{t('products.settings.katlamali.mechanismType')}</label>
                            <input type="text" placeholder={t('products.settings.katlamali.mechanismType')} className="modern-settings-input" value={newKatlamaliMekanizma.type} onChange={(e) => setNewKatlamaliMekanizma({ ...newKatlamaliMekanizma, type: e.target.value })} />
                          </div>
                          <UsdPriceField label={t('products.prices.cost')} value={newKatlamaliMekanizma.cost} onChange={(e) => setNewKatlamaliMekanizma({ ...newKatlamaliMekanizma, cost: e.target.value })} exchangeRates={exchangeRates} style={{ flex: 1, minWidth: '100px' }} />
                          <UsdPriceField label={t('products.settings.katlamali.retailShort')} value={newKatlamaliMekanizma.price} onChange={(e) => setNewKatlamaliMekanizma({ ...newKatlamaliMekanizma, price: e.target.value })} exchangeRates={exchangeRates} style={{ flex: 1, minWidth: '100px' }} />
                          <UsdPriceField label={t('products.settings.katlamali.wholesaleShort')} value={newKatlamaliMekanizma.wholesale} onChange={(e) => setNewKatlamaliMekanizma({ ...newKatlamaliMekanizma, wholesale: e.target.value })} exchangeRates={exchangeRates} style={{ flex: 1, minWidth: '100px' }} />
                          <button
                            onClick={() => {
                              if (!newKatlamaliMekanizma.type.trim()) return;
                              const currentList = katlamaliExtras.mekanizmaList || [];
                              const newList = [...currentList, {
                                id: Date.now().toString(),
                                type: newKatlamaliMekanizma.type.trim(),
                                cost: parseFloat(newKatlamaliMekanizma.cost) || 0,
                                price: parseFloat(newKatlamaliMekanizma.price) || 0,
                                wholesale: parseFloat(newKatlamaliMekanizma.wholesale) || 0,
                                priceCurrency: PRODUCT_PRICE_CURRENCY_USD
                              }];
                              setKatlamaliExtras({ ...katlamaliExtras, mekanizmaList: newList });
                              handleSaveSettings({ katlamaliMekanizmaList: newList });
                              setNewKatlamaliMekanizma({ type: '', cost: '', price: '', wholesale: '' });
                            }}
                            className="btn-save"
                            style={{ height: '38px' }}
                          >
                            {t('common.add')}
                          </button>
                        </div>

                        {katlamaliExtras.mekanizmaList && katlamaliExtras.mekanizmaList.length > 0 ? (
                          <ul style={{ listStyle: 'none', padding: 0, margin: 0, border: '1px solid #e2e8f0', borderRadius: '8px', backgroundColor: 'white' }}>
                            {katlamaliExtras.mekanizmaList.map((mek, idx) => (
                              <li key={mek.id} style={{ display: 'flex', flexDirection: 'column', padding: '10px 12px', borderBottom: idx < katlamaliExtras.mekanizmaList.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ fontSize: '14px', fontWeight: '500', color: '#1e293b' }}>{mek.type}</span>
                                  <button onClick={() => {
                                    const newList = katlamaliExtras.mekanizmaList.filter(m => m.id !== mek.id);
                                    setKatlamaliExtras({ ...katlamaliExtras, mekanizmaList: newList });
                                    handleSaveSettings({ katlamaliMekanizmaList: newList });
                                  }} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}><i className="fas fa-trash"></i></button>
                                </div>
                                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginTop: '6px' }}>
                                  <small>{t('products.settings.katlamali.costShort')}: <ProductListPrice amount={mek.cost} product={mechanismPriceProduct(mek)} exchangeRates={exchangeRates} /></small>
                                  <small>{t('products.settings.katlamali.retailShortLabel')}: <ProductListPrice amount={mek.price} product={mechanismPriceProduct(mek)} exchangeRates={exchangeRates} /></small>
                                  <small>{t('products.settings.katlamali.wholesaleShortLabel')}: <ProductListPrice amount={mek.wholesale} product={mechanismPriceProduct(mek)} exchangeRates={exchangeRates} /></small>
                                </div>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p style={{ fontSize: '13px', color: '#64748b' }}>{t('products.settings.katlamali.noMechanism')}</p>
                        )}
                      </div>
                    )}
                  </div>
                  <button className="btn-save" onClick={() => handleSaveSettings()} disabled={isSavingSettings} style={{ borderRadius: '10px', marginTop: '15px' }}>
                    {isSavingSettings ? '...' : t('common.save')}
                  </button>
                </div>
              </div>
              {renderMotorSettings('katlamaliPerde')}
            </>
          )}
            </>
          )}
        </div>

        {/* Arama Kutusu */}
        <div className="product-search-bar">
          <div className="product-search-input-wrapper">
            <i className="fas fa-search product-search-icon"></i>
            <input
              type="text"
              className="product-search-input"
              placeholder={t('products.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className="product-search-clear"
                onClick={() => setSearchQuery('')}
                title={t('products.clearSearch')}
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
          {searchQuery && (
            <span className="product-search-result-count">
              {t('products.searchResults', { count: filteredProducts.length })}
            </span>
          )}

          <div className="product-search-actions">
            {(canManageProducts) && (isLoading || filteredProducts.length > 0 || searchQuery) && (
              <button className="btn-add-product" onClick={handleAddNew}>
                <i className="fas fa-plus"></i> {t('products.addProduct')}
              </button>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="product-loading">
            <i className="fas fa-spinner fa-spin"></i> {t('products.loading')}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="product-empty-state">
            <i className="fas fa-box-open"></i>
            <p>{t('products.emptyCategory')}</p>
            {(canManageProducts) && (
              <button className="btn-add-product-empty" onClick={handleAddNew}>
                <i className="fas fa-plus"></i> {t('products.addFirstProduct')}
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="product-table-container" key={`product-table-${ratesVersion}`}>
              <ProductListTable
                filteredProducts={filteredProducts}
                activeTypeTab={activeTypeTab}
                canManageProducts={canManageProducts}
                handleEdit={handleEdit}
                setDeleteModal={setDeleteModal}
                formatPrice={formatPrice}
                exchangeRates={exchangeRates}
              />
            </div>

            <div className="product-card-list" key={`product-cards-${ratesVersion}`}>
              {filteredProducts.map((product, index) => (
                <div key={product.id} className="product-card">
                  <div className="product-card-header">
                    <span className="product-card-num">#{index + 1}</span>
                    <span className="product-card-name">{product.name}</span>
                    {(canManageProducts) && (
                      <div className="product-actions">
                        <button
                          className="action-btn edit"
                          onClick={() => handleEdit(product)}
                          title="Düzenle"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          className="action-btn delete"
                          onClick={() => setDeleteModal(product)}
                          title="Sil"
                        >
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="product-card-body">
                    {activeTypeTab === 'ahsapJaluzi' ? (
                      <div className="product-card-jaluzi-grid">
                        {[
                          { label: 'M. İpli', cost: product.merdivenIpliMaliyet, price: product.merdivenIpliFiyat, wholesale: product.merdivenIpliToptan },
                          { label: 'Kurdeleli', cost: product.kurdeleliMaliyet, price: product.kurdeleliFiyat, wholesale: product.kurdeleliToptan },
                          { label: 'M. İpli Red.', cost: product.merdivenIpliReduktorluMaliyet, price: product.merdivenIpliReduktorluFiyat, wholesale: product.merdivenIpliReduktorluToptan },
                          { label: 'Kur. Red.', cost: product.kurdelaliReduktorluMaliyet, price: product.kurdelaliReduktorluFiyat, wholesale: product.kurdelaliReduktorluToptan }
                        ].map((variant, i) => (
                          <div key={i} className="jaluzi-card-variant">
                            <span className="variant-label">{variant.label}</span>
                            <div className="variant-prices">
                              <span className="v-price cost" title="Maliyet"><ProductListPrice amount={variant.cost} product={product} exchangeRates={exchangeRates} /></span>
                              <span className="v-price retail" title="Perakende"><ProductListPrice amount={variant.price} product={product} exchangeRates={exchangeRates} /></span>
                              <span className="v-price wholesale" title="Toptan"><ProductListPrice amount={variant.wholesale} product={product} exchangeRates={exchangeRates} /></span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : activeTypeTab === 'katlamaliPerde' ? (
                      <>
                        {product.unitPrice > 0 && (
                          <span className="price-badge">M. İpli: <ProductListPrice amount={product.unitPrice} product={product} exchangeRates={exchangeRates} /></span>
                        )}
                        {product.kurdeleliFiyat > 0 && (
                          <span className="price-badge">Kurdeleli: <ProductListPrice amount={product.kurdeleliFiyat} product={product} exchangeRates={exchangeRates} /></span>
                        )}
                      </>
                    ) : activeTypeTab === 'rustik' ? (
                      <>
                        <span className="product-card-type">{t('constants.productTypes.rustik')}</span>
                        {isStockTrackedType(activeTypeTab) && (
                          <span className={`stock-badge${isStockTrackedType(activeTypeTab) && isLowStock(product.stock, activeTypeTab) ? ' stock-low' : ''}`}>
                            {t('products.table.stock')}: {formatStockDisplay(product.stock, product.productType, t, formatPrice)}
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        {product.cost > 0 && (
                          <span className="cost-badge">Maliyet: <ProductListPrice amount={product.cost} product={product} exchangeRates={exchangeRates} /></span>
                        )}
                        {product.unitPrice > 0 && (
                          <span className="price-badge">Satış: <ProductListPrice amount={product.unitPrice} product={product} exchangeRates={exchangeRates} /></span>
                        )}
                        {product.wholesalePrice > 0 && (
                          <span className="price-badge wholesale-badge">Toptan: <ProductListPrice amount={product.wholesalePrice} product={product} exchangeRates={exchangeRates} /></span>
                        )}
                        {isStockTrackedType(activeTypeTab) && (
                          <span className={`stock-badge${isStockTrackedType(activeTypeTab) && isLowStock(product.stock, activeTypeTab) ? ' stock-low' : ''}`}>
                            {t('products.table.stock')}: {formatStockDisplay(product.stock, product.productType, t, formatPrice)}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && handleCancel()}>
          <div className="product-form-modal">
            <div className="modal-header">
              <h3>{editingProduct ? t('products.form.editTitle') : t('products.form.addTitle')}</h3>
              <button className="modal-close" onClick={handleCancel}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleSave} className="product-form">
              <div className="form-group">
                <label>{t('products.form.productType')}</label>
                <select
                  value={formData.productType}
                  onChange={(e) => setFormData(prev => ({ ...prev, productType: e.target.value }))}
                  disabled={isSaving}
                >
                  {Object.entries(PRODUCT_TYPES).map(([key, type]) => (
                    <option key={key} value={key}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>{t('products.form.productName')}</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="örn. Karya Düz Tül"
                  disabled={isSaving}
                  autoFocus
                />
              </div>

              {isStockTrackedType(formData.productType) && (
                <div className="form-group">
                  <label>
                    {usesMeterStock(formData.productType)
                      ? t('products.form.stockMeters')
                      : t('products.form.stockPieces')}
                  </label>
                  <input
                    type="number"
                    step={usesMeterStock(formData.productType) ? '0.01' : '1'}
                    min="0"
                    value={formData.stock}
                    onChange={(e) => setFormData(prev => ({ ...prev, stock: e.target.value }))}
                    disabled={isSaving}
                    placeholder="0"
                  />
                </div>
              )}

              {(formData.productType === 'ahsapJaluzi') ? (
                <div className="jaluzi-price-grid">
                  <h5 className="section-subtitle">{t('products.form.dynamicPricingUsd')}</h5>

                  <div className="jaluzi-grid-row form-row-3">
                    <UsdPriceField label={`${t('products.table.merdivenIpli')} — ${t('products.prices.cost')}`} value={formData.merdivenIpliMaliyet} onChange={(e) => setFormData(prev => ({ ...prev, merdivenIpliMaliyet: e.target.value }))} disabled={isSaving} exchangeRates={exchangeRates} />
                    <UsdPriceField label={`${t('products.table.merdivenIpli')} — ${t('products.prices.retail')}`} value={formData.merdivenIpliFiyat} onChange={(e) => setFormData(prev => ({ ...prev, merdivenIpliFiyat: e.target.value }))} disabled={isSaving} exchangeRates={exchangeRates} />
                    <UsdPriceField label={<><i className="fas fa-tags" style={{ color: '#f59e0b', marginRight: '4px' }}></i>{t('products.table.merdivenIpli')} — {t('products.prices.wholesale')}</>} value={formData.merdivenIpliToptan} onChange={(e) => setFormData(prev => ({ ...prev, merdivenIpliToptan: e.target.value }))} disabled={isSaving} exchangeRates={exchangeRates} style={{ borderColor: '#f59e0b' }} />
                  </div>

                  <div className="jaluzi-grid-row form-row-3">
                    <UsdPriceField label={`${t('products.table.kurdeleli')} — ${t('products.prices.cost')}`} value={formData.kurdeleliMaliyet} onChange={(e) => setFormData(prev => ({ ...prev, kurdeleliMaliyet: e.target.value }))} disabled={isSaving} exchangeRates={exchangeRates} />
                    <UsdPriceField label={`${t('products.table.kurdeleli')} — ${t('products.prices.retail')}`} value={formData.kurdeleliFiyat} onChange={(e) => setFormData(prev => ({ ...prev, kurdeleliFiyat: e.target.value }))} disabled={isSaving} exchangeRates={exchangeRates} />
                    <UsdPriceField label={<><i className="fas fa-tags" style={{ color: '#f59e0b', marginRight: '4px' }}></i>{t('products.table.kurdeleli')} — {t('products.prices.wholesale')}</>} value={formData.kurdeleliToptan} onChange={(e) => setFormData(prev => ({ ...prev, kurdeleliToptan: e.target.value }))} disabled={isSaving} exchangeRates={exchangeRates} style={{ borderColor: '#f59e0b' }} />
                  </div>

                  <div className="jaluzi-grid-row form-row-3">
                    <UsdPriceField label={`${t('products.table.merdivenIpliRed')} — ${t('products.prices.cost')}`} value={formData.merdivenIpliReduktorluMaliyet} onChange={(e) => setFormData(prev => ({ ...prev, merdivenIpliReduktorluMaliyet: e.target.value }))} disabled={isSaving} exchangeRates={exchangeRates} />
                    <UsdPriceField label={`${t('products.table.merdivenIpliRed')} — ${t('products.prices.retail')}`} value={formData.merdivenIpliReduktorluFiyat} onChange={(e) => setFormData(prev => ({ ...prev, merdivenIpliReduktorluFiyat: e.target.value }))} disabled={isSaving} exchangeRates={exchangeRates} />
                    <UsdPriceField label={<><i className="fas fa-tags" style={{ color: '#f59e0b', marginRight: '4px' }}></i>{t('products.table.merdivenIpliRed')} — {t('products.prices.wholesale')}</>} value={formData.merdivenIpliReduktorluToptan} onChange={(e) => setFormData(prev => ({ ...prev, merdivenIpliReduktorluToptan: e.target.value }))} disabled={isSaving} exchangeRates={exchangeRates} style={{ borderColor: '#f59e0b' }} />
                  </div>

                  <div className="jaluzi-grid-row form-row-3">
                    <UsdPriceField label={`${t('products.table.kurdeleliRed')} — ${t('products.prices.cost')}`} value={formData.kurdelaliReduktorluMaliyet} onChange={(e) => setFormData(prev => ({ ...prev, kurdelaliReduktorluMaliyet: e.target.value }))} disabled={isSaving} exchangeRates={exchangeRates} />
                    <UsdPriceField label={`${t('products.table.kurdeleliRed')} — ${t('products.prices.retail')}`} value={formData.kurdelaliReduktorluFiyat} onChange={(e) => setFormData(prev => ({ ...prev, kurdelaliReduktorluFiyat: e.target.value }))} disabled={isSaving} exchangeRates={exchangeRates} />
                    <UsdPriceField label={<><i className="fas fa-tags" style={{ color: '#f59e0b', marginRight: '4px' }}></i>{t('products.table.kurdeleliRed')} — {t('products.prices.wholesale')}</>} value={formData.kurdelaliReduktorluToptan} onChange={(e) => setFormData(prev => ({ ...prev, kurdelaliReduktorluToptan: e.target.value }))} disabled={isSaving} exchangeRates={exchangeRates} style={{ borderColor: '#f59e0b' }} />
                  </div>

                </div>
              ) : formData.productType === 'rustik' ? (
                <div className="rustik-info-msg" style={{ marginBottom: '15px', color: '#64748b', fontSize: '0.85rem' }}>
                  <i className="fas fa-info-circle"></i> Rustik ürünleri için fiyatlandırma, sipariş sırasında ayak ve aksesuarlar üzerinden otomatik hesaplanır. Burada sadece ürün adı girilmelidir.
                </div>
              ) : (
                <div className="form-row-3">
                  <UsdPriceField
                    label={t('products.form.cost')}
                    value={formData.cost}
                    onChange={(e) => setFormData(prev => ({ ...prev, cost: e.target.value }))}
                    disabled={isSaving}
                    exchangeRates={exchangeRates}
                  />
                  <UsdPriceField
                    label={t('products.form.unitPriceRetail')}
                    value={formData.unitPrice}
                    onChange={(e) => setFormData(prev => ({ ...prev, unitPrice: e.target.value }))}
                    disabled={isSaving}
                    exchangeRates={exchangeRates}
                  />
                  <UsdPriceField
                    label={<><i className="fas fa-tags" style={{ color: '#f59e0b', marginRight: '5px' }}></i>{t('products.form.wholesalePrice')}</>}
                    value={formData.wholesalePrice}
                    onChange={(e) => setFormData(prev => ({ ...prev, wholesalePrice: e.target.value }))}
                    disabled={isSaving}
                    exchangeRates={exchangeRates}
                    style={{ borderColor: '#f59e0b' }}
                  />
                </div>
              )}

              {formError && (
                <div className="form-error">
                  <i className="fas fa-exclamation-circle"></i> {formError}
                </div>
              )}
              {formSuccess && (
                <div className="form-success">
                  <i className="fas fa-check-circle"></i> {formSuccess}
                </div>
              )}

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={handleCancel} disabled={isSaving}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn-save" disabled={isSaving}>
                  {isSaving ? (
                    <><i className="fas fa-spinner fa-spin"></i> {t('common.saving')}</>
                  ) : (
                    <><i className="fas fa-save"></i> {editingProduct ? t('common.update') : t('common.save')}</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteModal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setDeleteModal(null)}>
          <div className="delete-modal">
            <div className="modal-header">
              <h3>Ürün Sil</h3>
              <button className="modal-close" onClick={() => setDeleteModal(null)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="delete-modal-body">
              <i className="fas fa-exclamation-triangle warning-icon"></i>
              <p>
                <strong>"{deleteModal.name}"</strong> adlı ürünü silmek istediğinizden emin misiniz?
              </p>
              <p className="delete-warning">Bu işlem geri alınamaz.</p>
            </div>
            <div className="delete-modal-actions">
              <button className="btn-cancel" onClick={() => setDeleteModal(null)}>
                İptal
              </button>
              <button className="btn-delete-confirm" onClick={handleDelete}>
                <i className="fas fa-trash"></i> Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {excelModal.show && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setExcelModal({ show: false })}>
          <div className="excel-modal">
            <div className="modal-header">
              <h3>{excelModal.title}</h3>
              <button className="modal-close" onClick={() => setExcelModal({ show: false })}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="excel-modal-body">
              {excelModal.type === 'error' && <i className="fas fa-times-circle error-icon"></i>}
              {excelModal.type === 'success' && <i className="fas fa-check-circle success-icon"></i>}
              {excelModal.type === 'confirm' && <i className="fas fa-exclamation-triangle warning-icon"></i>}
              {excelModal.type === 'info' && <i className="fas fa-info-circle info-icon"></i>}
              {excelModal.type === 'confirm' && <h4>{t('products.modal.confirmationRequired')}</h4>}
              <p style={{ whiteSpace: 'pre-line' }}>{excelModal.message}</p>
            </div>
            <div className="excel-modal-actions">
              {excelModal.type === 'confirm' ? (
                <>
                  <button className="btn-cancel" onClick={() => { setExcelModal({ show: false }); setIsExcelLoading(false); }}>
                    {t('common.cancel')}
                  </button>
                  <button className="btn-save" onClick={excelModal.onConfirm}>
                    {t('products.modal.yesUpload')}
                  </button>
                </>
              ) : (
                <button className="btn-save" onClick={() => setExcelModal({ show: false })}>
                  {t('common.ok')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductList;
